import React, { useState, useMemo, useEffect, useRef } from 'react';
import { scalarText, extractionLines, isNested } from '../../utils/extractionValue';
import axios from 'axios';
import { hasReport } from '../../constants/creditPullStatus';
import { isOpenNeed } from '../../constants/needStatus';
import { STATUS_COLORS } from '../../constants/statusColors';
import { NOTE_MIN_CHARS as SEND_BACK_MIN, STAFF_NOTE_LABEL, noteTooShort } from '../../constants/loxSendBackStaff';
import { usePlacedFlags, useModuleItems, FraudBanner, T as GT, card as GCARD, btn as GBTN } from './guidedReview/GuidedModuleSteps';

/**
 * ReviewModule — 6-step underwriting review wizard.
 *
 * Layered ON TOP of the existing Review tab content (UWSnapshotCard,
 * AI Extracted Data, ReviewBorrowerGroup grid, existing Atlas card,
 * Approve Borrower banner). The wizard is the guided path; the
 * existing surfaces remain the canonical edit detail layer.
 *
 * Steps (in order — SmartResume opens on lowest incomplete):
 *   1. Identity   (primary + co_identity)
 *   2. Income     (primary + co_income)   — hosts ApplyAndRepricePanel
 *   3. Assets     (primary + co_assets)   — read-only, points to Income
 *   4. Housing    (primary + co_housing)
 *   5. Credit     (primary + co_credit as sub-section)
 *   6. Misc       (primary + co_misc)
 *
 * Per-step body = SectionReviewPanel rendering primary + co-borrower
 * side by side, each with their own Accept / Flag. Both buttons call
 * POST /api/review/admin/:id/section { section, action, note? }
 * (the existing endpoint) and surface results via onSectionUpdated().
 */

export const STEPS = [
  { id: 'identity', label: 'Identity',      shortLabel: 'ID',
    primary: 'identity', co: 'co_identity' },
  { id: 'income',   label: 'Income',        shortLabel: 'Income',
    primary: 'income',   co: 'co_income',    hasReprice: true },
  { id: 'assets',   label: 'Assets',        shortLabel: 'Assets',
    primary: 'assets',   co: 'co_assets',    readOnlyFinancials: true },
  { id: 'housing',  label: 'Housing',       shortLabel: 'Housing',
    primary: 'housing',  co: 'co_housing' },
  { id: 'credit',   label: 'Credit',        shortLabel: 'Credit',
    primary: 'credit',   co: 'co_credit' },
  { id: 'misc',     label: 'Miscellaneous', shortLabel: 'Misc',
    primary: 'misc',     co: 'co_misc' },
];

// 🔑 COMPENSATING FACTORS: ONE LIST, AFM v2 (2026-10-21). The labels staff record and each factor's threshold come
// from the comp-factor API (afmMatrix COMPENSATING_FACTORS / COMP_FACTOR_LIST) — nothing is typed here. The old desk
// keys (dp20, reserves6, …) are retired; a factor saved under one on users.uw_comp_factor shows read-only, as saved.

// Miscellaneous step is borrower-conditional — only shows when the
// borrower has Declaration LOXes (foreclosure / bankruptcy / employment
// gap / mortgage default), is rent-free, or has uploaded miscellaneous
// docs. Otherwise the wizard collapses to 5 steps so admin doesn't
// click through an empty Miscellaneous review step on clean files.
export function getActiveSteps(hasMisc) {
  return hasMisc ? STEPS : STEPS.filter(s => s.id !== 'misc');
}

// Pure derivation — first non-accepted primary section. 'complete'
// once every step's primary section is accepted/approved. stepsList
// defaults to STEPS for backward compat; pass a filtered list (via
// getActiveSteps) to skip the Misc step on borrowers without
// Misc-section items.
export function deriveReviewStep(sections, stepsList = STEPS) {
  for (let i = 0; i < stepsList.length; i++) {
    const s = sections?.find(r => r.section === stepsList[i].primary);
    if (!s || !['accepted','approved'].includes(s.status)) {
      return i + 1;
    }
  }
  return 'complete';
}

// Document category → document_type substrings. Used to filter the
// merged `documents` array (which already contains both borrowers
// tagged with owner_type) down to the per-step relevant subset.
//
// Misc is a strict whitelist matching the borrower-side Verification
// Misc section (Verification.js:6612-6716) — the 4 declaration LOXes
// + loan-officer-requested `misc_req_*` items. Previously misc was a
// catch-all (anything not in the other categories), which leaked
// `lox_letter` rows from the ReadyCheck credit-conditions flow and
// other unmatched types into the underwriting Misc Review.
const DOC_CATEGORIES = {
  identity: ['drivers_license','passport','government_id','photo_id','identity','id_document','state_id'],
  income:   ['w2','pay_stub','paystub','tax_return','1099','offer_letter','profit_loss','employment_verification','self_employment'],
  assets:   ['bank_statement','investment','retirement','gift_letter','asset','checking','savings'],
  housing:  ['vor','rental_history','landlord_letter','mortgage_statement','verification_of_rent','vom'],
  credit:   ['credit_report'],
  misc:     ['bankruptcy_lox','foreclosure_lox','employment_gap_lox','mortgage_default_lox','misc_req_'],
};

function filterDocsForCategory(docs, category) {
  if (!docs?.length) return [];
  const types = DOC_CATEGORIES[category] || [];
  return docs.filter(d => {
    const t = (d.document_type || '').toLowerCase();
    return types.some(known => t.includes(known));
  });
}

// ───────────────────────────────────────────────────────────────
// SectionReviewPanel — renders the borrower column(s) for a step.
// ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = ({
    accepted: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', label: '✓ Accepted' },
    approved: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', label: '✓ Approved' },
    flagged:  { bg: '#fef2f2', color: '#991b1b', border: '#fca5a5', label: '⚠ Flagged' },
    pending:  { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: '○ Pending' },
    locked:   { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb', label: '🔒 Locked' },
  })[status] || { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb', label: status || 'Unknown' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      borderRadius: 20, padding: '3px 10px',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function BorrowerColumn({
  user, isPrimary, sectionKey, sectionRow,
  filteredDocs, primaryUserForReadycheck,
  loxConditions, borrowerProfileId,
  category, headers, API, onSectionUpdated,
}) {
  // Per-LOX flag-and-resend state — separate from the section-level
  // flag state below. Each row keys by condition_id so independent
  // rows don't share UI. { open, note, busy, error } per row.
  const [loxFlagState, setLoxFlagState] = useState({});
  const [flagNotes, setFlagNotes] = useState('');
  const [flagging, setFlagging]   = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError]         = useState('');

  // 🔑 AFM v2 (2026-10-21): a score in the exception band is no longer documented here. The credit check raises a
  // credit condition that needs a recorded compensating factor (source_flag.compFactorsRequired, conditionKey
  // 'fico_band'), recorded in the factor panel on that condition like every other factor rule — one list, one table,
  // and Accept is refused until it is recorded. A factor saved here before v2 (users.uw_comp_factor) stays visible.

  // 🔑 CONFIRM ASSETS RECORDS AN AMOUNT (Jesse, 2026-10-26 — backend utils/staffAssetConfirm): each account's verified
  // figure, pre-filled from what the document check read; $0 only through "No usable assets" with a reason.
  const isAssetsSection = sectionKey === 'assets' || sectionKey === 'co_assets';
  const [assetConfirm, setAssetConfirm] = useState({ sources: null, amounts: {}, noUsable: false, reason: '' });
  useEffect(() => {
    if (!isAssetsSection || ['accepted', 'approved'].includes(sectionRow?.status || '') || !user) return;
    const targetId = primaryUserForReadycheck?.id || user.id;
    axios.get(`${API}/api/review/admin/${targetId}/asset-confirm${sectionKey === 'co_assets' ? '?co=1' : ''}`, { headers })
      .then((r) => {
        const sources = (r.data && r.data.sources) || [];
        const amounts = {};
        for (const x of sources) amounts[x.id] = x.prefill != null ? String(x.prefill) : '';
        setAssetConfirm((c) => ({ ...c, sources, amounts }));
      })
      .catch(() => setAssetConfirm((c) => ({ ...c, sources: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssetsSection, sectionRow?.status, user && user.id, sectionKey]);

  if (!user) return null;
  const status   = sectionRow?.status || 'pending';
  const accepted = ['accepted','approved'].includes(status);

  const callSection = async (action, note, extra = {}) => {
    setError('');
    try {
      // Section endpoint takes the PRIMARY user id in the URL but the
      // section name carries the co_ prefix for co-borrower rows. That
      // matches the existing handler signature in backend/routes/review.js.
      const targetId = primaryUserForReadycheck?.id || user.id;
      await axios.post(
        `${API}/api/review/admin/${targetId}/section`,
        { section: sectionKey, action, note, ...extra },
        { headers }
      );
      onSectionUpdated && onSectionUpdated();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Section update failed.');
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    await callSection('accept', null, isAssetsSection
      ? { amounts: assetConfirm.amounts, no_usable_assets: assetConfirm.noUsable, no_usable_reason: assetConfirm.reason } : {});
    setAccepting(false);
  };
  const handleFlag = async () => {
    if (!flagNotes.trim()) return;
    setFlagging(true);
    await callSection('flag', flagNotes.trim());
    setFlagging(false);
  };

  // ReadyCheck finding — only for the PRIMARY borrower. The
  // readycheckSummary builder runs primary-only, so co_* columns
  // never show AI findings (per the build correction). Returns
  // both the per-section finding AND the full parsed analysis so
  // the summary block below can also surface globals like
  // incomeAssessment.qualifyingMonthly.
  const { readycheckFinding, fullAnalysis } = (() => {
    if (!isPrimary) return { readycheckFinding: null, fullAnalysis: null };
    const rc = user?.readycheck;
    if (!rc || !rc.phase1?.latestAnalysis) {
      return { readycheckFinding: null, fullAnalysis: null };
    }
    let parsed = null;
    try {
      const raw = rc.phase1.latestAnalysis.analysis_json
               || rc.phase1.latestAnalysis.analysis;
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (_) { return { readycheckFinding: null, fullAnalysis: null }; }
    if (!parsed?.sections) return { readycheckFinding: null, fullAnalysis: parsed };
    return {
      readycheckFinding: parsed.sections.find(s => s.category === category) || null,
      fullAnalysis: parsed,
    };
  })();

  // Rich section summary — pulls everything the AI surfaced for this
  // section into a single derivation so the render block can stay
  // declarative. The goal of the Summary card was originally just
  // counts ("found 6 paystubs"), but the AI's analysis_json carries
  // far more — narrative reasoning, calculation breakdowns, per-doc
  // verdicts, calculated qualifying figures — and admins were asking
  // "how did the AI determine that?" without seeing that data
  // surfaced anywhere. This block exposes the full per-section AI
  // output inline.
  const sectionSummary = (() => {
    // Doc-type counts — keep the existing chip strip so admins see
    // the underlying evidence count alongside the AI narrative.
    const docTypeCounts = {};
    (filteredDocs || []).forEach(d => {
      const t = (d.document_type || 'other').toLowerCase();
      docTypeCounts[t] = (docTypeCounts[t] || 0) + 1;
    });
    const docChips = Object.entries(docTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([t, n]) => ({ label: t.replace(/_/g, ' '), count: n }));

    // AI per-doc findings (items[]) — surfaced in full now, not just
    // non-good ones. Admins want to see what the AI APPROVED too,
    // not just what it flagged, so they can verify the AI saw
    // everything that's actually on file.
    const items = readycheckFinding?.items || [];
    const aiGood    = items.filter(i => i.status === 'good').length;
    const aiWarning = items.filter(i => i.status === 'warning').length;
    const aiConcern = items.filter(i => i.status === 'concern' || i.status === 'flag').length;
    const aiMissing = items.filter(i => i.status === 'missing').length;

    // Income-specific — the calculation reasoning (breakdown) is the
    // big "how was this determined" surface. Plus confidence and the
    // AI's free-form notes.
    const incomeAss = (category === 'income') ? fullAnalysis?.incomeAssessment : null;

    // Asset sources — read straight from users.asset_sources JSONB
    // (the canonical store). Per-source breakdown surfaces what the
    // borrower entered + summed total. The AI itself doesn't return
    // an assetAssessment block today, so we show the raw source-level
    // breakdown to give admins the "how was the total calculated"
    // signal that incomeAssessment provides for income.
    const assetSources = (category === 'assets')
      ? (() => {
          try {
            const raw = user?.asset_sources;
            if (!raw) return [];
            return Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : []);
          } catch (_) { return []; }
        })()
      : [];
    const assetTotal = assetSources.reduce((s, a) => s + (Number(a?.amount) || 0), 0);

    // Section-scoped missing docs — the AI returns a top-level
    // missingDocs[] (covering all sections), but each missing entry
    // typically reads like "1099 — Q4 of last year". We can't be
    // perfect about per-section attribution without category tags
    // from the AI, so we show ALL missing here only on the catch-all
    // misc category; for other sections, the items[] entries with
    // status='missing' already cover the per-section gaps.
    const allMissing = Array.isArray(fullAnalysis?.missingDocs) ? fullAnalysis.missingDocs : [];
    const sectionMissing = (category === 'misc') ? allMissing : [];

    return {
      docChips, items,
      aiGood, aiWarning, aiConcern, aiMissing,
      hasAnyAi: items.length > 0,
      sectionStatus: readycheckFinding?.status || null,
      sectionMessage: readycheckFinding?.message || null,
      incomeAss,
      assetSources, assetTotal,
      sectionMissing,
      employmentConcern: (category === 'income') ? fullAnalysis?.employmentConcern : null,
    };
  })();

  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: '#f9fafb',
      borderRadius: 10, padding: '14px 16px',
    }}>
      {/* Header: name + status */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 12, gap: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: '#6b7280', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 2,
          }}>
            {isPrimary ? '👤 Primary' : '👤 Co-Borrower'}
          </div>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#111827',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user.first_name} {user.last_name}
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* AI Analysis Summary — detailed per-section breakdown.
          Surfaces the AI's reasoning, not just counts. Goal: an
          admin reading this card should understand HOW the AI
          arrived at its verdict for this section without having
          to dig through the raw analysis_json. Primary-only because
          the AI run is primary-scoped (per readycheckSummary
          builder in admin.js). */}
      {isPrimary && (
        sectionSummary.docChips.length > 0
        || sectionSummary.hasAnyAi
        || sectionSummary.incomeAss
        || sectionSummary.assetSources.length > 0
        || sectionSummary.sectionMessage
        || sectionSummary.sectionMissing.length > 0
        // Credit step opens the card when there's a credit pull on
        // file even if the AI didn't return a credit section in its
        // analysis_json (the AI's Phase 1 prompt focuses on docs +
        // income — credit data lives on xactus_* columns and surfaces
        // independently via the Credit-specific block below).
        || (category === 'credit' && !!(user?.xactus_equifax_score || user?.xactus_experian_score || user?.xactus_transunion_score || user?.fico_score_actual))
      ) && (() => {
        // Map AI section status → theme for the card header pill.
        const statusTheme = {
          good:    { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', label: '✓ AI: Good' },
          warning: { bg: '#fffbeb', text: '#92400e', border: '#fde68a', label: '⚠ AI: Warning' },
          concern: { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5', label: '! AI: Concern' },
        };
        const sTheme = sectionSummary.sectionStatus
          && statusTheme[sectionSummary.sectionStatus];
        // Item status → row glyph + color, used for the per-doc
        // findings list below. Includes 'good' so admins see what
        // the AI approved, not just what it flagged.
        const itemTheme = (s) => ({
          good:    { glyph: '✓', color: '#166534' },
          warning: { glyph: '⚠', color: '#92400e' },
          concern: { glyph: '!', color: '#991b1b' },
          flag:    { glyph: '!', color: '#991b1b' },
          missing: { glyph: '✗', color: '#dc2626' },
        })[s] || { glyph: '•', color: '#6b7280' };
        return (
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '12px 14px',
          marginBottom: 10, fontSize: 12,
        }}>
          {/* Card header: title + section-status pill */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, marginBottom: 10,
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: '#6b7280',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              AI Analysis Summary
            </div>
            {sTheme && (
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '2px 8px', borderRadius: 10,
                background: sTheme.bg, color: sTheme.text,
                border: `1px solid ${sTheme.border}`,
              }}>
                {sTheme.label}
              </span>
            )}
          </div>

          {/* INCOME-specific: highlighted qualifying-income calc with
              the AI's breakdown narrative + confidence + notes. This
              is the centerpiece for the income step — admins want to
              see HOW the AI got to the qualifying figure. */}
          {sectionSummary.incomeAss && (
            <div style={{
              padding: '10px 12px', marginBottom: 10,
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderLeft: '3px solid #16a34a', borderRadius: 6,
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: 6, marginBottom: 4, flexWrap: 'wrap',
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#166534' }}>
                  AI Qualifying Income: ${Number(sectionSummary.incomeAss.qualifyingMonthly || 0).toLocaleString()}/mo
                </div>
                {sectionSummary.incomeAss.confidence && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Confidence: {sectionSummary.incomeAss.confidence}
                  </span>
                )}
              </div>
              {sectionSummary.incomeAss.breakdown && (
                <div style={{
                  fontSize: 11, color: '#374151', lineHeight: 1.5,
                  marginTop: 6, marginBottom: sectionSummary.incomeAss.notes ? 6 : 0,
                }}>
                  <span style={{ fontWeight: 700, color: '#166534' }}>How calculated:</span>{' '}
                  {sectionSummary.incomeAss.breakdown}
                </div>
              )}
              {sectionSummary.incomeAss.notes && (
                <div style={{
                  fontSize: 11, color: '#374151', lineHeight: 1.5, marginTop: 4,
                }}>
                  <span style={{ fontWeight: 700, color: '#166534' }}>Notes:</span>{' '}
                  {sectionSummary.incomeAss.notes}
                </div>
              )}
              {sectionSummary.employmentConcern && (
                <div style={{
                  marginTop: 8, padding: '6px 10px',
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4,
                  fontSize: 11, color: '#92400e',
                }}>
                  <strong>Employment concern: </strong>
                  {sectionSummary.employmentConcern.message || sectionSummary.employmentConcern.reason || JSON.stringify(sectionSummary.employmentConcern)}
                </div>
              )}
            </div>
          )}

          {/* ASSETS-specific: total + per-source breakdown showing
              the borrower's entered sources. Mirrors the "how was
              this determined" framing the income block uses — sum
              of per-source amounts is the visible math. */}
          {category === 'assets' && sectionSummary.assetSources.length > 0 && (
            <div style={{
              padding: '10px 12px', marginBottom: 10,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderLeft: '3px solid #3a6ad9', borderRadius: 6,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 800, color: '#1e40af',
                marginBottom: 6,
              }}>
                Total Liquid Assets: ${Number(sectionSummary.assetTotal).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>
                Breakdown by source:
              </div>
              {sectionSummary.assetSources.map((src, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 6,
                  fontSize: 11, color: '#374151', padding: '3px 0',
                  borderBottom: i < sectionSummary.assetSources.length - 1 ? '1px solid #dbeafe' : 'none',
                }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong>{src.source_name || src.institution_name || src.source_type || 'Source'}</strong>
                    {(src.method || src.source_type) && (
                      <span style={{ color: '#6b7280', marginLeft: 4 }}>
                        · {(src.method || src.source_type).replace(/_/g, ' ')}
                      </span>
                    )}
                  </span>
                  <span style={{ fontWeight: 700, color: '#1e40af', whiteSpace: 'nowrap' }}>
                    ${Number(src.amount || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* CREDIT-specific: file snapshot from the Xactus pull —
              qualifying mid FICO, per-bureau scores, tradeline +
              collections counts, bankruptcy/foreclosure flags, and
              any AI-extracted derog flags. Same "how was this
              determined" framing as the income / assets boxes: the
              visible math is the bureau-score average → mid-FICO and
              the tradeline/collection counts that flow into DTI
              calculations downstream. Only renders when a credit
              pull has actually happened — otherwise the box would
              show a row of em-dashes and add no signal. */}
          {category === 'credit' && (() => {
            const eq = user?.xactus_equifax_score;
            const ex = user?.xactus_experian_score;
            const tu = user?.xactus_transunion_score;
            const hasPull = !!(eq || ex || tu) || hasReport(user?.xactus_pull_status);
            if (!hasPull) return null;
            const midFico = user?.fico_score_actual;
            const tradelines = user?.xactus_tradeline_count;
            const collections = Number(user?.xactus_collections_count || 0);
            const bk = !!user?.xactus_bankruptcy_flag;
            const fc = !!user?.xactus_foreclosure_flag;
            const derogs = (() => {
              try {
                const raw = user?.xactus_derog_flags;
                if (!raw) return [];
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
              } catch (_) { return []; }
            })();
            const pulledAt = user?.date_credit_pulled || user?.fico_pulled_at;
            const scoreColor = (s) => s == null
              ? '#9ca3af'
              : s >= 700 ? '#16a34a'
              : s >= 620 ? '#92400e'
              : '#dc2626';
            return (
              <div style={{
                padding: '10px 12px', marginBottom: 10,
                background: '#eff6ff', border: '1px solid #bfdbfe',
                borderLeft: '3px solid #3a6ad9', borderRadius: 6,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline',
                  justifyContent: 'space-between', gap: 6, marginBottom: 6, flexWrap: 'wrap',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#1e40af' }}>
                    Qualifying FICO: <span style={{ color: scoreColor(midFico) }}>{midFico || '—'}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginLeft: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      (mid)
                    </span>
                  </div>
                  {pulledAt && (
                    <span style={{ fontSize: 10, color: '#6b7280' }}>
                      Pulled {new Date(pulledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
                {/* Per-bureau breakdown — visible math behind the mid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                  {[['EQ', eq], ['EX', ex], ['TU', tu]].map(([label, score]) => (
                    <div key={label} style={{
                      textAlign: 'center', padding: '5px 0',
                      background: '#fff', border: '1px solid #bfdbfe', borderRadius: 4,
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor(score) }}>{score ?? '—'}</div>
                    </div>
                  ))}
                </div>
                {/* Tradeline + risk counters */}
                <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
                  <div>
                    <strong style={{ color: '#1e40af' }}>Tradelines:</strong> {tradelines ?? '—'}
                    {' · '}
                    <strong style={{ color: collections > 0 ? '#dc2626' : '#1e40af' }}>Collections:</strong>{' '}
                    <span style={{ color: collections > 0 ? '#dc2626' : '#374151' }}>{collections}</span>
                  </div>
                  <div>
                    <strong style={{ color: bk ? '#dc2626' : '#1e40af' }}>Bankruptcy:</strong>{' '}
                    <span style={{ color: bk ? '#dc2626' : '#374151' }}>{bk ? 'Flagged' : 'None'}</span>
                    {' · '}
                    <strong style={{ color: fc ? '#dc2626' : '#1e40af' }}>Foreclosure:</strong>{' '}
                    <span style={{ color: fc ? '#dc2626' : '#374151' }}>{fc ? 'Flagged' : 'None'}</span>
                  </div>
                </div>
                {/* AI-extracted derogatory flag list */}
                {derogs.length > 0 && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px',
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4,
                    fontSize: 11, color: '#7f1d1d',
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>
                      Derogatory flags ({derogs.length})
                    </div>
                    {derogs.slice(0, 5).map((d, i) => (
                      <div key={i}>• {typeof d === 'string' ? d : (d?.label || d?.type || JSON.stringify(d))}</div>
                    ))}
                    {derogs.length > 5 && (
                      <div style={{ fontStyle: 'italic' }}>+ {derogs.length - 5} more</div>
                    )}
                  </div>
                )}
                {/* LOX summary row — pointer down to the per-LOX block below in the Credit step. */}
                {Array.isArray(loxConditions) && loxConditions.length > 0 && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px',
                    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4,
                    fontSize: 11, color: '#92400e',
                  }}>
                    <strong>LOX queue:</strong> {loxConditions.length} on file
                    {' · '}
                    {(() => {
                      const open = loxConditions.filter(c => !c.lox_completed && !['accepted','resolved','dismissed','rejected'].includes(c.status)).length;
                      const responded = loxConditions.filter(c => c.lox_completed && !['accepted','resolved','dismissed','rejected'].includes(c.status)).length;
                      return `${open} open · ${responded} responded`;
                    })()}
                    {' · see Letters of Explanation below'}
                  </div>
                )}

                {/* 🔑 HISTORY ONLY (AFM v2, 2026-10-21): a compensating factor documented on this file before v2, under
                    the retired desk list. A new one is recorded on the credit condition's factor panel. */}
                {(() => {
                  const savedKey = user?.uw_comp_factor || '';
                  if (!savedKey) return null;
                  const savedAt = user?.uw_comp_factor_at;
                  return (
                    <div data-testid="uw-comp-factor-history" style={{
                      marginTop: 10, padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
                      fontSize: 11, color: '#374151', lineHeight: 1.5,
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                        Compensating factor documented before AFM v2
                      </div>
                      {savedKey}
                      {savedAt && <span style={{ color: '#6b7280' }}> · by {user.uw_comp_factor_by || 'UW'} · {new Date(savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* AI section-level narrative. This is the AI's free-form
              read on the section as a whole — what stood out, what
              was solid, what's a soft flag. Always rendered when
              present, regardless of section. */}
          {sectionSummary.sectionMessage && (
            <div style={{
              padding: '8px 12px', marginBottom: 10,
              background: '#f9fafb', border: '1px solid #e5e7eb',
              borderRadius: 6,
              fontSize: 11, color: '#374151', lineHeight: 1.55,
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                AI Narrative
              </div>
              {sectionSummary.sectionMessage}
            </div>
          )}

          {/* Per-document findings — full list (good + non-good) so
              admins see what the AI saw at the doc level. Previously
              only non-good items rendered; admins couldn't tell
              whether the AI had inspected a particular doc or
              skipped it entirely. */}
          {sectionSummary.hasAnyAi && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>
                Per-document Findings ({sectionSummary.items.length})
              </div>
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 6, padding: '6px 10px',
              }}>
                {sectionSummary.items.map((item, j) => {
                  const t = itemTheme(item.status);
                  return (
                    <div key={j} style={{
                      display: 'flex', gap: 6, alignItems: 'flex-start',
                      fontSize: 11, padding: '3px 0',
                      borderBottom: j < sectionSummary.items.length - 1 ? '1px dashed #e5e7eb' : 'none',
                    }}>
                      <span style={{ color: t.color, fontWeight: 700, flexShrink: 0, width: 12 }}>
                        {t.glyph}
                      </span>
                      <span style={{ flex: 1, color: '#374151', lineHeight: 1.4 }}>
                        <strong style={{ color: t.color }}>{item.label}</strong>
                        {item.note && (
                          <span style={{ color: '#6b7280' }}> — {item.note}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Roll-up status counts under the findings list. */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 10,
                fontSize: 10, color: '#6b7280',
                marginTop: 6,
              }}>
                {sectionSummary.aiGood > 0    && <span style={{ color: '#166534', fontWeight: 600 }}>✓ {sectionSummary.aiGood} good</span>}
                {sectionSummary.aiWarning > 0 && <span style={{ color: '#92400e', fontWeight: 600 }}>⚠ {sectionSummary.aiWarning} warning{sectionSummary.aiWarning === 1 ? '' : 's'}</span>}
                {sectionSummary.aiConcern > 0 && <span style={{ color: '#991b1b', fontWeight: 600 }}>! {sectionSummary.aiConcern} concern{sectionSummary.aiConcern === 1 ? '' : 's'}</span>}
                {sectionSummary.aiMissing > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>✗ {sectionSummary.aiMissing} missing</span>}
              </div>
            </div>
          )}

          {/* Section-scoped missing docs — only shown on misc since
              the AI's top-level missingDocs[] doesn't carry category
              tags. Per-section gaps are already covered by the items
              with status='missing' above. */}
          {sectionSummary.sectionMissing.length > 0 && (
            <div style={{
              padding: '8px 12px', marginBottom: 10,
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderLeft: '3px solid #dc2626', borderRadius: 6,
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                Missing Documents ({sectionSummary.sectionMissing.length})
              </div>
              {sectionSummary.sectionMissing.map((m, i) => (
                <div key={i} style={{ fontSize: 11, color: '#991b1b', lineHeight: 1.4, padding: '2px 0' }}>
                  • {typeof m === 'string' ? m : (m?.label || JSON.stringify(m))}
                </div>
              ))}
            </div>
          )}

          {/* Doc-type evidence count — kept as a compact chip strip
              at the bottom of the card. Lets admin verify "the AI
              made its call based on this many docs of this type"
              alongside the per-doc findings list above. */}
          {sectionSummary.docChips.length > 0 && (
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>
                Documents on File
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {sectionSummary.docChips.map((c, i) => (
                  <span key={i} style={{
                    fontSize: 10, fontWeight: 600,
                    background: '#f3f4f6', color: '#374151',
                    padding: '2px 8px', borderRadius: 12,
                    border: '1px solid #e5e7eb',
                    textTransform: 'capitalize', whiteSpace: 'nowrap',
                  }}>
                    {c.count} × {c.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {/* ReadyCheck finding (primary only) */}
      {readycheckFinding && (
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '10px 12px',
          marginBottom: 12, fontSize: 12,
          color: '#374151', lineHeight: 1.5,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700,
            color: '#6b7280', marginBottom: 4,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Document check finding
          </div>
          {readycheckFinding.message}
          {readycheckFinding.items?.filter(i => i.status !== 'good').map((item, j) => (
            <div key={j} style={{
              marginTop: 4, fontSize: 11,
              color: item.status === 'missing' ? '#dc2626' : '#92400e',
            }}>
              • {item.label}{item.note ? ` — ${item.note}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* LOX list — credit step only, primary only. Each credit_conditions row (the underwriter's ask) beside the
          borrower's response. The Tier 1 letter review is shown in LoxReviewList (utils/loxReview). */}
      {isPrimary && category === 'credit' && Array.isArray(loxConditions) && loxConditions.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '10px 12px',
          marginBottom: 12, fontSize: 12,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: '#6b7280',
            marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Letters of Explanation ({loxConditions.length})
          </div>
          {loxConditions.map((c) => {
            const hasResponse = !!(c.borrower_response || '').trim();
            // Visual state of the LOX row. Three buckets:
            //   accepted/resolved/dismissed → green (✓ Accepted callout)
            //   open (no response)          → amber/yellow (existing default)
            //   awaiting-revision           → amber (also default)
            // Without the green state, clicking Accept appeared to do
            // nothing — the button hid but the row stayed amber, so
            // admins thought the click failed. Now the entire row's
            // chrome flips green on success and shows a "✓ Accepted"
            // pill at the top, with the borrower response preserved
            // for audit.
            const isFinalAccepted = ['accepted', 'resolved', 'dismissed'].includes(c.status);
            const rowStyle = isFinalAccepted
              ? { padding: '10px 12px', marginBottom: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '3px solid #16a34a', borderRadius: 6 }
              : { padding: '10px 12px', marginBottom: 8, background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '3px solid #fbbf24', borderRadius: 6 };
            return (
              <div key={c.id} style={rowStyle}>
                {isFinalAccepted && (
                  <div style={{ marginBottom: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 10,
                      background: '#fff', color: '#166534',
                      border: '1px solid #bbf7d0',
                      textTransform: 'uppercase', letterSpacing: 0.3,
                    }}>
                      ✓ Accepted{c.resolved_at ? ` · ${new Date(c.resolved_at).toLocaleDateString()}` : ''}
                    </span>
                  </div>
                )}
                <div style={{
                  fontWeight: 600,
                  color: isFinalAccepted ? '#166534' : '#92400e',
                  marginBottom: 4, fontSize: 12,
                }}>
                  {c.title || 'Letter of Explanation'}
                </div>
                {loxAskLine(c) ? <div data-testid={`review-lox-ask-${c.id}`} style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{loxAskLine(c)}</div> : null}
                {c.description && (
                  <div style={{ fontSize: 11, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>
                    {c.description}
                  </div>
                )}
                {hasResponse ? (
                  <div style={{
                    marginTop: 6, padding: '6px 10px',
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderLeft: '3px solid #92400e', borderRadius: 4,
                    fontSize: 11,
                  }}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: 0.4,
                      marginBottom: 3,
                    }}>
                      Borrower Response
                    </div>
                    <div style={{ color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                      {c.borrower_response}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 4 }}>
                    Awaiting borrower response.
                  </div>
                )}
                {/* 🔑 Compensating factors this condition requires (2026-10-20): recorded by underwriting, each with a note. */}
                <CompFactorPanel condition={c} borrowerId={borrowerProfileId} headers={headers} api={API} onChange={onSectionUpdated} />
                {hasResponse && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {/* Per-LOX Flag & Send Back — bounces this row
                          back to the borrower with the admin's reason
                          attached. Clicking expands an inline note
                          input; submitting fires the new flag-and-resend
                          endpoint which resets lox_completed, creates an additional_needs
                          row, alerts the borrower, and emails them. */}
                      {(() => {
                        const flagState = loxFlagState[c.id] || {};
                        const isFlagOpen = !!flagState.open;
                        if (isFlagOpen) return null; // hide buttons while textarea is showing
                        // Accept clears this individual LOX without
                        // sending it back to the borrower — admin
                        // reviewed the response and approved it.
                        // Hits the existing PATCH /credit-conditions
                        // endpoint with action='accept' which sets
                        // status='accepted' + resolved=TRUE on the row.
                        // The accepted row drops out of openLOX so the
                        // wizard's Step 4 (Submit) gate unlocks if
                        // this was the last open LOX.
                        const acceptLox = async () => {
                          if (!borrowerProfileId || !c.id) return;
                          setLoxFlagState(prev => ({ ...prev, [c.id]: { open: false, note: '', busy: true, error: null, accepting: true } }));
                          try {
                            await axios.patch(
                              `${API}/api/admin/borrowers/${borrowerProfileId}/credit-conditions/${c.id}`,
                              { action: 'accept' },
                              { headers }
                            );
                            setLoxFlagState(prev => ({ ...prev, [c.id]: { open: false, note: '', busy: false, error: null } }));
                            onSectionUpdated && onSectionUpdated();
                          } catch (err) {
                            const msg = err?.response?.data?.error || err?.message || 'Accept failed.';
                            setLoxFlagState(prev => ({ ...prev, [c.id]: { open: false, note: '', busy: false, error: msg } }));
                          }
                        };
                        const isAccepting = !!flagState.accepting;
                        // Don't show Accept on rows that are already
                        // resolved/accepted — the row's outer status
                        // pill already shows the resolved state.
                        const alreadyResolved = ['accepted', 'resolved', 'dismissed'].includes(c.status);
                        return (
                          <>
                            {!alreadyResolved && (
                              <button
                                type="button"
                                onClick={acceptLox}
                                disabled={isAccepting}
                                title="Accept this LOX response and clear it from the open queue"
                                style={{
                                  fontSize: 10, fontWeight: 700,
                                  padding: '3px 10px',
                                  // Doorly green outline, matching the
                                  // section-level Accept treatment.
                                  background: isAccepting ? '#9ca3af' : '#fff',
                                  color: isAccepting ? '#fff' : '#166534',
                                  border: isAccepting ? 'none' : '1.5px solid #acf44e',
                                  borderRadius: 12, cursor: isAccepting ? 'wait' : 'pointer',
                                }}
                              >
                                {isAccepting ? 'Accepting…' : '✓ Accept'}
                              </button>
                            )}
                            {/* Flag also hides on accepted rows — the row is in a terminal state. */}
                            {!alreadyResolved && (
                              <button
                                type="button"
                                onClick={() => setLoxFlagState(prev => ({ ...prev, [c.id]: { open: true, note: '', busy: false, error: null } }))}
                                disabled={isAccepting}
                                style={{
                                  fontSize: 10, fontWeight: 700,
                                  padding: '3px 10px',
                                  background: '#fff', color: '#ff082f',
                                  border: '1.5px solid #ff082f',
                                  borderRadius: 12, cursor: isAccepting ? 'not-allowed' : 'pointer',
                                  opacity: isAccepting ? 0.5 : 1,
                                }}
                              >
                                🚩 Flag &amp; Send Back
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {/* Inline flag-and-resend editor — appears when the
                        admin clicks 🚩 Flag & Send Back on this row.
                        Captures the reason the borrower needs to
                        revise. Submit posts to the per-LOX endpoint,
                        then onSectionUpdated refetches the admin
                        payload so the row flips back to "awaiting
                        response" and the AI verdict pill clears. */}
                    {(() => {
                      const flagState = loxFlagState[c.id] || {};
                      if (!flagState.open) return null;
                      const submitFlag = async () => {
                        const reason = (flagState.note || '').trim();
                        if (noteTooShort(reason)) {
                          setLoxFlagState(prev => ({ ...prev, [c.id]: { ...flagState, error: `Write at least ${SEND_BACK_MIN} characters.` } }));
                          return;
                        }
                        setLoxFlagState(prev => ({ ...prev, [c.id]: { ...flagState, busy: true, error: null } }));
                        try {
                          await axios.post(
                            `${API}/api/admin/borrowers/${borrowerProfileId}/credit-conditions/${c.id}/flag-and-resend`,
                            { reason, staffNote: (flagState.staffNote || '').trim() },
                            { headers }
                          );
                          setLoxFlagState(prev => ({ ...prev, [c.id]: { open: false, note: '', busy: false, error: null } }));
                          onSectionUpdated && onSectionUpdated();
                        } catch (err) {
                          const msg = err?.response?.data?.error || err?.message || 'Flag failed.';
                          setLoxFlagState(prev => ({ ...prev, [c.id]: { ...flagState, busy: false, error: msg } }));
                        }
                      };
                      return (
                        <div style={{
                          marginTop: 8, padding: '8px 10px',
                          background: '#fef2f2', border: '1px solid #fecaca',
                          borderRadius: 6,
                        }}>
                          {/* 🔑 REQUIRED, AT LEAST SEND_BACK_MIN CHARACTERS (Jesse 2026-11-02 — constants/loxSendBackStaff; the route refuses the same). */}
                          <label htmlFor={`lox-flag-note-${c.id}`} style={{
                            display: 'block', fontSize: 11, fontWeight: 700,
                            color: '#1A3A6B', marginBottom: 4,
                          }}>
                            {STAFF_NOTE_LABEL}
                          </label>
                          <textarea
                            id={`lox-flag-note-${c.id}`}
                            data-testid={`lox-flag-note-${c.id}`}
                            required
                            minLength={SEND_BACK_MIN}
                            rows={3}
                            value={flagState.note || ''}
                            onChange={e => setLoxFlagState(prev => ({ ...prev, [c.id]: { ...flagState, note: e.target.value } }))}
                            placeholder="e.g. 'Please include the specific date the account went to collections and whether it's currently paid, on a payment plan, or disputed.'"
                            style={{
                              width: '100%', boxSizing: 'border-box',
                              padding: '6px 8px', fontSize: 12,
                              border: '1px solid #fca5a5', borderRadius: 4,
                              fontFamily: 'inherit', resize: 'vertical',
                              marginBottom: 2,
                            }}
                          />
                          <div data-testid={`lox-flag-note-count-${c.id}`} style={{ fontSize: 10.5, marginBottom: 6,
                            color: noteTooShort(flagState.note) ? '#ff082f' : '#64748B' }}>
                            {(flagState.note || '').trim().length} / {SEND_BACK_MIN} characters minimum
                          </div>
                          {/* 🚨 STAFF-ONLY NOTE (Jesse, 2026-10-28): saved to the file's staff notes, never sent to the borrower. */}
                          <label style={{
                            display: 'block', fontSize: 10, fontWeight: 700,
                            color: '#374151', textTransform: 'uppercase',
                            letterSpacing: 0.4, marginBottom: 4,
                          }}>
                            Staff-only note (optional)
                          </label>
                          <textarea
                            data-testid={`lox-flag-staff-note-${c.id}`}
                            rows={2}
                            value={flagState.staffNote || ''}
                            onChange={e => setLoxFlagState(prev => ({ ...prev, [c.id]: { ...flagState, staffNote: e.target.value } }))}
                            placeholder="Saved to this file's staff notes. Never sent to the borrower."
                            style={{
                              width: '100%', boxSizing: 'border-box',
                              padding: '6px 8px', fontSize: 12,
                              border: '1px solid #d1d5db', borderRadius: 4,
                              fontFamily: 'inherit', resize: 'vertical',
                              marginBottom: 6,
                            }}
                          />
                          {flagState.error && (
                            <div style={{
                              padding: '4px 8px', marginBottom: 6,
                              background: '#fee2e2', border: '1px solid #fca5a5',
                              borderRadius: 4, fontSize: 11, color: '#7f1d1d',
                            }}>
                              {flagState.error}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => setLoxFlagState(prev => ({ ...prev, [c.id]: { open: false, note: '', busy: false, error: null } }))}
                              disabled={flagState.busy}
                              style={{
                                fontSize: 10, fontWeight: 700,
                                padding: '4px 12px',
                                background: '#fff', color: '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: 12, cursor: flagState.busy ? 'wait' : 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={submitFlag}
                              data-testid={`lox-flag-send-${c.id}`}
                              disabled={flagState.busy || noteTooShort(flagState.note)}
                              style={{
                                fontSize: 10, fontWeight: 700,
                                padding: '4px 12px',
                                background: noteTooShort(flagState.note) ? '#e5e7eb' : '#ff082f',
                                color: noteTooShort(flagState.note) ? '#9ca3af' : '#fff',
                                border: 'none', borderRadius: 12,
                                cursor: flagState.busy || noteTooShort(flagState.note) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {flagState.busy ? 'Sending…' : '🚩 Send Back to Borrower'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Document list — each filename is a click target that opens
          the uploaded PDF/image in a new tab via the existing
          /borrowers/:id/documents/:docId/view endpoint. The endpoint
          gates on doc.user_id === :id, so co-borrower docs use the
          co-borrower's user.id (BorrowerColumn already filters
          documents to its own user before passing them in). Blob
          response → object URL → window.open mirrors the same
          pattern used by the Loan-tab DocMagic LE/CD viewer at
          AdminBorrowerProfile.jsx:8101. */}
      {filteredDocs?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: '#6b7280',
            marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Documents ({filteredDocs.length})
          </div>
          {filteredDocs.slice(0, 6).map((doc, j) => {
            const handleViewDoc = async () => {
              if (!doc?.id) return;
              try {
                const r = await axios.get(
                  `${API}/api/admin/borrowers/${user.id}/documents/${doc.id}/view`,
                  { headers, responseType: 'blob' }
                );
                const url = URL.createObjectURL(r.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (err) {
                alert('Unable to open document: ' + (err.response?.data?.error || err.message));
              }
            };
            return (
            <div key={j} style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', fontSize: 12, color: '#374151',
              padding: '4px 0',
              borderBottom: j < Math.min(filteredDocs.length, 6) - 1 ? '1px solid #f3f4f6' : 'none',
            }}>
              <button
                type="button"
                onClick={handleViewDoc}
                title="Open document in a new tab"
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#3a6ad9', textDecoration: 'underline',
                  cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', flex: 1, textAlign: 'left',
                }}
              >
                📄 {doc.filename || doc.original_filename || doc.document_type}
              </button>
              <span style={{
                fontSize: 10, marginLeft: 6,
                color: doc.status === 'approved' ? '#16a34a'
                  : doc.status === 'rejected' ? '#dc2626'
                  : '#6b7280',
              }}>
                {doc.status}
              </span>
            </div>
            );
          })}
          {filteredDocs.length > 6 && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              + {filteredDocs.length - 6} more — see Documents tab
            </div>
          )}
        </div>
      )}

      {isAssetsSection && !accepted && assetConfirm.sources && (
        <div data-testid="uw-asset-confirm" style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', marginBottom: 8, display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#000' }}>Verified amount per account</div>
          {assetConfirm.sources.length === 0 && <div style={{ fontSize: 12, color: '#000' }}>No accounts on file.</div>}
          {!assetConfirm.noUsable && assetConfirm.sources.map((x) => (
            <label key={x.id} data-testid="uw-asset-confirm-row" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#000', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 160px', minWidth: 0 }}>{x.label}
                <span style={{ display: 'block', fontSize: 11 }}>
                  {x.checkRead != null ? `Check read $${Number(x.checkRead).toLocaleString('en-US')}` : 'The check read no balance'} · entered ${Number(x.declared).toLocaleString('en-US')}
                </span>
                {x.reconfirm && (
                  <span data-testid="uw-asset-confirm-newer" style={{ display: 'block', fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                    Newer statement: {x.reconfirm.statementAmount != null ? `$${Number(x.reconfirm.statementAmount).toLocaleString('en-US')}` : 'not read yet'} · you confirmed ${Number(x.reconfirm.staffFigure).toLocaleString('en-US')} · the lower counts until you re-confirm
                  </span>
                )}
              </span>
              <input data-testid="uw-asset-confirm-amount" inputMode="decimal" value={assetConfirm.amounts[x.id] ?? ''}
                onChange={(e) => setAssetConfirm((c) => ({ ...c, amounts: { ...c.amounts, [x.id]: e.target.value } }))}
                style={{ width: 120, fontSize: 12, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6 }} />
            </label>
          ))}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#000' }}>
            <input type="checkbox" data-testid="uw-asset-confirm-none" checked={assetConfirm.noUsable}
              onChange={(e) => setAssetConfirm((c) => ({ ...c, noUsable: e.target.checked }))} />
            No usable assets
          </label>
          {assetConfirm.noUsable && (
            <input data-testid="uw-asset-confirm-none-reason" placeholder="Why (recorded on the file)" value={assetConfirm.reason}
              onChange={(e) => setAssetConfirm((c) => ({ ...c, reason: e.target.value }))}
              style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6 }} />
          )}
        </div>
      )}

      {/* Accept / Flag controls */}
      {accepted ? (
        <div style={{
          fontSize: 11, color: '#16a34a', fontWeight: 600,
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 6, padding: '6px 10px',
        }}>
          ✓ Accepted{sectionRow?.approved_at ? ` on ${new Date(sectionRow.approved_at).toLocaleDateString()}` : ''}
        </div>
      ) : status === 'flagged' ? (
        <div style={{
          fontSize: 11, color: '#991b1b',
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 6, padding: '8px 10px',
          marginBottom: 8,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Flagged</div>
          {sectionRow?.admin_note || sectionRow?.notes || 'Awaiting borrower response.'}
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={{
              marginTop: 8, padding: '5px 10px',
              // Outlined Doorly brand green — buttons stay unfilled
              // until the admin actually clicks Accept (whereupon the
              // status flips to 'accepted' and this entire branch
              // unmounts in favor of the green accepted callout).
              // Reads as a "ready but not yet committed" affordance
              // rather than a solid CTA, which over-emphasized the
              // action before it had been taken.
              background: '#fff', color: '#166534',
              border: '1.5px solid #acf44e', borderRadius: 6,
              fontSize: 11, fontWeight: 700,
              cursor: accepting ? 'wait' : 'pointer',
            }}
          >
            {accepting ? '…' : '✓ Accept (override flag)'}
          </button>
        </div>
      ) : (
        <div>
          <textarea
            value={flagNotes}
            onChange={e => setFlagNotes(e.target.value)}
            placeholder="Flag note (required to flag)…"
            rows={2}
            style={{
              width: '100%', padding: '6px 8px',
              border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 11, fontFamily: 'inherit',
              resize: 'vertical', boxSizing: 'border-box',
              marginBottom: 6,
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleAccept}
              disabled={accepting}
              style={{
                flex: 1,
                // Outlined Doorly brand green — see comment on the
                // override-flag variant above; this is the pending-
                // state Accept that gets clicked first for most rows.
                // Mid-click the button shows a gray fill, then the
                // accepted callout replaces the whole pair entirely.
                background: accepting ? '#9ca3af' : '#fff',
                color: accepting ? '#fff' : '#166534',
                border: accepting ? 'none' : '1.5px solid #acf44e',
                borderRadius: 6,
                padding: '7px 10px', fontSize: 11, fontWeight: 700,
                cursor: accepting ? 'wait' : 'pointer',
              }}
            >
              {accepting ? '…' : '✓ Accept'}
            </button>
            <button
              onClick={handleFlag}
              disabled={flagging || !flagNotes.trim()}
              style={{
                flex: 1,
                // Doorly brand red (#ff082f) — same palette the
                // pending-status pills and destructive admin actions
                // use. White text reads cleanly on the saturated red.
                background: !flagNotes.trim() ? '#e5e7eb' : (flagging ? '#9ca3af' : '#ff082f'),
                color: !flagNotes.trim() ? '#9ca3af' : '#fff',
                border: 'none', borderRadius: 6,
                padding: '7px 10px', fontSize: 11, fontWeight: 700,
                cursor: !flagNotes.trim() ? 'default' : (flagging ? 'wait' : 'pointer'),
              }}
            >
              {flagging ? '…' : '⚠ Flag'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 6, fontSize: 11, color: '#dc2626',
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 6, padding: '6px 8px',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function SectionReviewPanel({
  step, primaryUser, coBorrower, reviewSections,
  documents, loxConditions, category, headers, API, id, onSectionUpdated,
}) {
  const primaryDocs = useMemo(
    () => filterDocsForCategory(
      (documents || []).filter(d => d.owner_type !== 'coborrower'),
      category
    ),
    [documents, category]
  );
  const coDocs = useMemo(
    () => filterDocsForCategory(
      (documents || []).filter(d => d.owner_type === 'coborrower'),
      category
    ),
    [documents, category]
  );

  const primarySectionRow = reviewSections?.find(s => s.section === step.primary);
  const coSectionRow      = step.co
    ? reviewSections?.find(s => s.section === step.co)
    : null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: coBorrower ? '1fr 1fr' : '1fr',
      gap: 14,
    }}>
      <BorrowerColumn
        user={primaryUser}
        isPrimary={true}
        sectionKey={step.primary}
        sectionRow={primarySectionRow}
        filteredDocs={primaryDocs}
        primaryUserForReadycheck={primaryUser}
        loxConditions={loxConditions}
        borrowerProfileId={id}
        category={category}
        headers={headers}
        API={API}
        onSectionUpdated={onSectionUpdated}
      />
      {coBorrower && step.co && (
        <BorrowerColumn
          user={coBorrower}
          isPrimary={false}
          sectionKey={step.co}
          sectionRow={coSectionRow}
          filteredDocs={coDocs}
          primaryUserForReadycheck={primaryUser}
          category={category}
          headers={headers}
          API={API}
          onSectionUpdated={onSectionUpdated}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// ApplyAndRepricePanel — Income step only.
// ───────────────────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
function ApplyAndRepricePanel({ user, headers, API, id, onSectionUpdated }) {
  const [form, setForm] = useState({
    income: user.uw_override_income != null ? String(user.uw_override_income)
          : (user.gross_monthly_income != null ? String(user.gross_monthly_income) : ''),
    assets: user.uw_override_assets != null ? String(user.uw_override_assets)
          : (user.liquid_assets != null ? String(user.liquid_assets) : ''),
    debt:   user.uw_override_debt != null ? String(user.uw_override_debt)
          : (user.total_monthly_debt != null ? String(user.total_monthly_debt) : ''),
    fico:   user.uw_override_fico != null ? String(user.uw_override_fico)
          : (user.fico_score_actual != null ? String(user.fico_score_actual) : ''),
    notes:  user.uw_override_notes || '',
  });
  const [saving, setSaving]   = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError]     = useState('');
  const [response, setResponse] = useState(null);

  const submit = async (pushToBorrower) => {
    if (!form.notes?.trim()) {
      setError('Override rationale is required for the audit trail.');
      return;
    }
    setError('');
    const setBusy = pushToBorrower ? setPushing : setSaving;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/borrowers/${id}/uw-override`,
        { ...form, pushToBorrower: !!pushToBorrower },
        { headers }
      );
      setResponse(data);
      onSectionUpdated && onSectionUpdated();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const fmt$ = v => v != null ? `$${Number(v).toLocaleString()}` : '—';
  const fmtPct = v => v != null ? `${(Number(v) * 100).toFixed(2)}%` : '—';

  return (
    <div style={{
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 10, padding: '16px 18px',
      marginTop: 16,
    }}>
      <div style={{
        fontSize: 13, fontWeight: 700,
        color: '#111827', marginBottom: 4,
      }}>
        Apply &amp; Reprice — Income / Financials
      </div>
      <div style={{
        fontSize: 11, color: '#6b7280', marginBottom: 12,
      }}>
        Save first to see the before/after impact. Then push to the
        borrower when ready — push requires Atlas pricing accepted.
      </div>

      <div style={{
        display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 10,
      }}>
        {[
          { key: 'income', label: 'Monthly Income ($)' },
          { key: 'assets', label: 'Liquid Assets ($)' },
          { key: 'debt',   label: 'Monthly Debt ($)' },
          { key: 'fico',   label: 'FICO Override' },
        ].map(f => (
          <div key={f.key}>
            <label style={{
              fontSize: 11, color: '#6b7280',
              display: 'block', marginBottom: 2,
            }}>
              {f.label}
            </label>
            <input
              type="number"
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={{
                width: '100%', padding: '6px 8px',
                border: '1px solid #d1d5db', borderRadius: 6,
                fontSize: 12, boxSizing: 'border-box',
              }}
            />
          </div>
        ))}
      </div>

      <textarea
        value={form.notes}
        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
        placeholder="Override rationale — required for audit trail…"
        rows={2}
        style={{
          width: '100%', padding: '6px 8px',
          border: '1px solid #d1d5db', borderRadius: 6,
          fontSize: 12, fontFamily: 'inherit',
          resize: 'vertical', boxSizing: 'border-box',
          marginBottom: 10,
        }}
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => submit(false)}
          disabled={saving || pushing || !form.notes?.trim()}
          style={{
            flex: 1,
            background: saving ? '#9ca3af' : (form.notes?.trim() ? '#111827' : '#e5e7eb'),
            color: form.notes?.trim() ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 8,
            padding: '9px 14px', fontSize: 12, fontWeight: 600,
            cursor: !form.notes?.trim() ? 'default' : (saving ? 'wait' : 'pointer'),
          }}
        >
          {saving ? 'Saving…' : '💾 Save Override — Don\'t Push'}
        </button>
        <button
          onClick={() => submit(true)}
          disabled={saving || pushing || !form.notes?.trim()}
          style={{
            flex: 1,
            background: pushing ? '#9ca3af' : (form.notes?.trim() ? '#acf44e' : '#e5e7eb'),
            color: form.notes?.trim() ? '#111827' : '#9ca3af',
            border: 'none', borderRadius: 8,
            padding: '9px 14px', fontSize: 12, fontWeight: 700,
            cursor: !form.notes?.trim() ? 'default' : (pushing ? 'wait' : 'pointer'),
          }}
        >
          {pushing ? 'Pushing…' : '🚀 Save & Push to Borrower'}
        </button>
      </div>

      {error && (
        <div style={{
          marginTop: 10, fontSize: 12, color: '#dc2626',
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 6, padding: '8px 10px',
        }}>
          {error}
        </div>
      )}

      {/* Before/after comparison from the response */}
      {response?.before && response?.after && (
        <div style={{
          marginTop: 14,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8, overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
          }}>
            {['Metric','Before','After'].map(h => (
              <div key={h} style={{
                padding: '8px 12px', fontSize: 11,
                fontWeight: 700, color: '#6b7280',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>{h}</div>
            ))}
          </div>
          {[
            { label: 'Max Purchase Price', key: 'maxPurchasePrice', fmt: fmt$,   better: 'higher' },
            { label: 'Final Rate',         key: 'finalRate',        fmt: fmtPct, better: 'lower'  },
            { label: 'Monthly Payment',    key: 'monthlyPayment',   fmt: fmt$,   better: 'lower'  },
            { label: 'DTI',                key: 'dti',              fmt: fmtPct, better: 'lower'  },
            { label: 'Cash to Close',      key: 'cashToClose',      fmt: fmt$,   better: 'lower'  },
            { label: 'Eligibility',        key: 'eligibility',      fmt: v => v || '—', better: null },
          ].map((row, i) => {
            const b = response.before[row.key];
            const a = response.after[row.key];
            const changed = b !== a;
            const improved = row.better && b != null && a != null && Number.isFinite(Number(a))
              ? (row.better === 'higher' ? Number(a) > Number(b) : Number(a) < Number(b))
              : false;
            const color = !changed ? '#6b7280'
              : (improved ? '#16a34a' : '#dc2626');
            return (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr',
                borderBottom: '1px solid #f3f4f6',
              }}>
                <div style={{ padding: '8px 12px', fontSize: 12, color: '#374151', fontWeight: 500 }}>
                  {row.label}
                </div>
                <div style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>
                  {row.fmt(b)}
                </div>
                <div style={{
                  padding: '8px 12px', fontSize: 12,
                  color, fontWeight: changed ? 700 : 500,
                }}>
                  {row.fmt(a)}{changed && row.better && (
                    <span style={{ marginLeft: 4 }}>{improved ? '↑' : '↓'}</span>
                  )}
                </div>
              </div>
            );
          })}
          {response.pushed && (
            <div style={{
              padding: '8px 12px',
              background: '#f0fdf4',
              fontSize: 11, fontWeight: 600, color: '#166534',
              borderTop: '1px solid #bbf7d0',
            }}>
              ✓ Pushed to borrower dashboard.
            </div>
          )}
          {response.pushError && (
            <div style={{
              padding: '8px 12px',
              background: '#fef2f2',
              fontSize: 11, fontWeight: 600, color: '#991b1b',
              borderTop: '1px solid #fca5a5',
            }}>
              ⚠ Override saved but push failed: {response.pushError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────
// AIExtractionsPanel — collapsible reference panel for the
// AI-parsed document data the borrower's uploads produced. Lives
// inside the Underwriting Module body so admins can consult /
// approve / reject extractions in-context with the step review.
//
// Default open when at least one extraction is in `pending_review`,
// so the pending decision is impossible to miss. Otherwise default
// closed so it doesn't push the step content down the page.
// ───────────────────────────────────────────────────────────────
// CreditReviewCard — Step 5 (Credit) dedicated card. Replaces the
// generic SectionAIReviewCard on Credit because the relevant data
// lives in xactus-specific columns (xactus_equifax_score / experian /
// transunion + fico_score_actual + xactus_tradeline_count + the
// derog/collections/bankruptcy/foreclosure flag set), not in the
// Phase 1 analysis_json.sections[] block the generic card reads from.
// ReadyCheck Phase 3 produces the credit pull AFTER Phase 1 is sealed,
// so the credit data never makes it into analysis_json.
//
// Layout still matches IncomeCalculationCard / AssetsCalculationCard:
//   Header   →  "Credit Review" + accepted/flagged pill
//   Body     →  qualifying mid FICO (headline) + tri-bureau breakdown
//                + tradelines / collections / derogs summary
//   Empty    →  amber explainer when Xactus hasn't been pulled
//   Actions  →  Approve credit  |  Flag
// ── 🔑 THE TIER 1 LETTER REVIEW, FOR STAFF (Jesse, 2026-10-21 — backend/utils/loxReview, AFM v2 §8) ───────────────
// Does the letter address the item it was requested for. "Addresses" shows as such; anything else is an open medium flag
// with what the letter misses and one click to "Request a revised letter" — a neutral message staff can edit, sent
// through the letter's existing send-back (flag-and-resend), so the borrower gets the normal notification with the staff
// text. It never accepts, rejects or clears anything by itself, and nothing of it reaches the borrower.
const LOX_VERDICT_LABEL = {
  addresses: 'Letter addresses the request',
  partially_addresses: 'Letter partly addresses the request',
  does_not_address: 'Letter does not address the request',
  unreadable: 'Letter could not be read automatically',
  could_not_run: 'The automatic check could not run',
};
const asList = (v) => { if (Array.isArray(v)) return v; try { const p = JSON.parse(v || '[]'); return Array.isArray(p) ? p : []; } catch (_) { return []; } };
// 🚨 The caller passes no title (Jesse 2026-11-02): the staff condition title must never land in a message the borrower
// sees exactly as written, so the draft says "this item".
export function revisionMessage(title, missing) {
  const items = (missing || []).map((m) => m.charAt(0).toLowerCase() + m.slice(1));
  return `Thank you for your letter about ${title || 'this item'}. Please send a revised letter that also covers:\n`
    + (items.length ? items.map((m) => `• ${m}`).join('\n') : '• what happened, when, why, whether it is resolved, and why it is not expected to happen again')
    + '\n\nYour own words are all we need.';
}
// `actions` false: the letters list shows the actions itself, right-aligned in one row.
function LoxReviewNote({ c, borrowerId, headers, api, onChange, actions = true }) {
  const r = c.lox_review;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!r) return <div data-testid={`lox-review-${c.id}`} data-verdict="none" style={{ color: '#9ca3af', marginTop: 4 }}>Letter check: not run yet.</div>;
  const ok = r.verdict === 'addresses';
  const missing = asList(r.items_missing);
  const isOpen = r.flag_status === 'open';
  const rerun = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${api}/api/admin/borrowers/${borrowerId}/credit-conditions/${c.id}/lox-review`, {}, { headers });
      onChange && (await onChange());
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Could not run it.'); } finally { setBusy(false); }
  };
  const btn = { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 5, cursor: busy ? 'wait' : 'pointer' };
  return (
    <div data-testid={`lox-review-${c.id}`} data-verdict={r.verdict} data-flag={r.flag_status}
      style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, background: ok ? '#f0fdf4' : '#fffbeb', border: `1px solid ${ok ? '#bbf7d0' : '#fde68a'}` }}>
      <div style={{ fontWeight: 700, color: ok ? '#166534' : '#92400e' }}>
        {ok ? '✓ ' : ''}{LOX_VERDICT_LABEL[r.verdict] || r.verdict}
        {!ok && isOpen ? <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#fde68a', color: '#78350f' }}>MEDIUM</span> : null}
        {!ok && !isOpen && r.resolution ? <span style={{ marginLeft: 6, fontWeight: 500, color: '#6b7280' }}>({String(r.resolution).replace(/_/g, ' ')})</span> : null}
      </div>
      {r.staff_summary ? <div style={{ color: '#374151', marginTop: 2 }}>{r.staff_summary}</div> : null}
      {!ok && missing.length ? (
        <div data-testid={`lox-review-missing-${c.id}`} style={{ marginTop: 4, color: '#78350f' }}>
          Missing: {missing.join(' · ')}
        </div>
      ) : null}
      {actions && !ok && isOpen && !open ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <button type="button" data-testid={`lox-request-revision-${c.id}`} disabled={busy}
            onClick={() => setOpen(true)}
            style={{ ...btn, border: 'none', background: '#92400e', color: '#fff' }}>Request a revised letter</button>
          {['could_not_run', 'unreadable'].includes(r.verdict) ? (
            <button type="button" data-testid={`lox-review-rerun-${c.id}`} disabled={busy} onClick={rerun}
              style={{ ...btn, border: '1px solid #92400e', background: '#fff', color: '#92400e' }}>{busy ? 'Working…' : 'Run the check again'}</button>
          ) : null}
        </div>
      ) : null}
      {!actions && ['could_not_run', 'unreadable'].includes(r.verdict) ? (
        <button type="button" data-testid={`lox-review-rerun-${c.id}`} disabled={busy} onClick={rerun}
          style={{ ...btn, marginTop: 6, border: '1px solid #92400e', background: '#fff', color: '#92400e' }}>{busy ? 'Working…' : 'Run the check again'}</button>
      ) : null}
      {open ? <RevisionForm c={c} missing={missing} borrowerId={borrowerId} headers={headers} api={api} onChange={onChange} onClose={() => setOpen(false)} /> : null}
      {err ? <div role="alert" style={{ color: '#991b1b', marginTop: 4 }}>{err}</div> : null}
    </div>
  );
}

// The "Request a revised letter" form (one definition: the review note's flagged path and the letters list's action).
function RevisionForm({ c, missing, borrowerId, headers, api, onChange, onClose }) {
  const [msg, setMsg] = useState('');
  // The neutral draft, never the staff condition title (one place: revisionMessage).
  useEffect(() => { setMsg(revisionMessage(null, missing)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [staffNote, setStaffNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const btn = { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 5, cursor: busy ? 'wait' : 'pointer' };
  const send = async () => {
    if (noteTooShort(msg)) { setErr(`Write at least ${SEND_BACK_MIN} characters.`); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${api}/api/admin/borrowers/${borrowerId}/credit-conditions/${c.id}/flag-and-resend`, { reason: msg.trim(), staffNote: staffNote.trim() }, { headers });
      setStaffNote(''); onClose && onClose();
      onChange && (await onChange());
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Could not send it.'); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 6 }}>
      <label htmlFor={`lox-revision-message-${c.id}`} style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#1A3A6B' }}>{STAFF_NOTE_LABEL}</label>
      <textarea id={`lox-revision-message-${c.id}`} data-testid={`lox-revision-message-${c.id}`} required minLength={SEND_BACK_MIN} rows={6} value={msg} onChange={(e) => setMsg(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: 6, border: '1px solid #fde68a', borderRadius: 4, fontFamily: 'inherit' }} />
      <div data-testid={`lox-revision-message-count-${c.id}`} style={{ fontSize: 10.5, color: noteTooShort(msg) ? '#ff082f' : '#64748B' }}>
        {msg.trim().length} / {SEND_BACK_MIN} characters minimum
      </div>
      {/* 🚨 STAFF-ONLY NOTE (Jesse, 2026-10-28): saved to the file's staff notes, never sent to the borrower. */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 6 }}>Staff-only note (optional)</div>
      <textarea data-testid={`lox-revision-staff-note-${c.id}`} rows={2} value={staffNote} onChange={(e) => setStaffNote(e.target.value)}
        placeholder="Saved to this file's staff notes. Never sent to the borrower."
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontFamily: 'inherit' }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" disabled={busy} onClick={() => onClose && onClose()} style={{ ...btn, border: '1px solid #d1d5db', background: '#fff', color: '#374151' }}>Cancel</button>
        <button type="button" data-testid={`lox-revision-send-${c.id}`} disabled={busy || noteTooShort(msg)} onClick={send}
          style={{ ...btn, border: 'none', background: !noteTooShort(msg) ? '#92400e' : '#e5e7eb', color: !noteTooShort(msg) ? '#fff' : '#9ca3af' }}>{busy ? 'Sending…' : 'Send to the borrower'}</button>
      </div>
      {err ? <div role="alert" style={{ color: '#991b1b', marginTop: 4 }}>{err}</div> : null}
    </div>
  );
}

// ── 🔑 LETTERS OF EXPLANATION ON THE CREDIT STEP (Jesse, 2026-10-21) ─────────────────────────────────────────────
// The module's LOX list lived in BorrowerColumn, which nothing renders, so a condition that needs compensating factors
// (2x30-day mortgage lates, a recent collection) could be accepted nowhere it could be satisfied: Accept answered 409
// COMP_FACTORS_REQUIRED and the factor panel was unreachable. Here: each letter, its factors, and Accept once answered.
// 🔑 AUTOMATIC LOX PHASE 1 (backend utils/loxAsks staffStatesFor, on the profile payload as c.lox_ask): that the borrower
// was asked on step 7, and the letter's state — open, answered, or held (holding the decision). Staff wording only.
export function loxAskLine(c) {
  const a = c && c.lox_ask;
  if (!a) return null;
  const state = a.state === 'answered' ? `answered${a.answeredWith === 'upload' ? ' with an uploaded letter' : a.answeredWith === 'text' ? ' in writing' : ''}`
    : a.state === 'held' ? 'open — holding the decision' : 'open';
  const told = a.notifiedAt ? ` · told after step 7 on ${new Date(a.notifiedAt).toLocaleDateString()}` : '';
  return `Asked automatically on step 7 · ${state}${told}`;
}

// 🔑 THE LETTERS LIST, ONE ROW PER ITEM (Hannah's review 2026-11-02 — screen 8 was rejected as not DoorlyDesk and messy).
// The Module's own look (./guidedReview/GuidedModuleSteps: T, card, btn; Guided1003Step's pill): each row is a plain title
// (backend utils/loxAsks staffTitleFor — the approved item phrase, never "LOX + 1 compensating factor — …"), ONE status pill
// and ONE meta line. Expanded: the letter (and the guided answers), the letter check's result, the compensating factors
// (read-only from the file; "Add factor" behind a link), and only the actions that apply, right-aligned — none while
// waiting on the borrower. A row with an action to take opens expanded.
const LOX_PILL = {
  waiting: { bg: GT.tan, fg: GT.ink, text: 'WAITING ON BORROWER' }, answered: { bg: GT.tan, fg: GT.ink, text: 'ANSWERED' },
  sent_back: { bg: GT.slateLight, fg: GT.ink, text: 'SENT BACK' }, accepted: { bg: GT.green, fg: GT.ink, text: 'ACCEPTED' },
  factor: { bg: GT.tan, fg: GT.ink, text: 'NEEDS A FACTOR' },
};
export function loxMetaLine(c) {
  const d = (t) => { const x = t ? new Date(t) : null; return x && !Number.isNaN(+x) ? x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''; };
  if (c.condition_type !== 'lox') return `From the credit report · ${d(c.created_at)}`;
  if (c.lox_ask) return `Asked automatically on step 7 · ${d(c.lox_ask.notifiedAt || c.created_at)}`;
  return `Asked on step 7 · ${d(c.sent_at || c.created_at)}`;
}
function LoxRow({ c, borrowerId, headers, api, onChange }) {
  const status = c.staff_status || (c.condition_type !== 'lox' ? 'factor' : c.lox_completed ? 'answered' : 'waiting');
  const [open, setOpen] = useState(status === 'answered' || status === 'factor');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [revising, setRevising] = useState(false);
  const pill = LOX_PILL[status] || LOX_PILL.waiting;
  const isLetter = c.condition_type === 'lox';
  const accept = async () => {
    setBusy(true); setError(null);
    try {
      await axios.patch(`${api}/api/admin/borrowers/${borrowerId}/credit-conditions/${c.id}`, { action: 'accept' }, { headers });
      onChange && (await onChange());
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || 'Could not accept it.');
    } finally { setBusy(false); }
  };
  const missing = asList(c.lox_review && c.lox_review.items_missing);
  const canAccept = status === 'answered' || status === 'factor';
  const canRevise = isLetter && status === 'answered';
  return (
    <div data-testid={`credit-lox-${c.id}`} data-status={status} style={{ borderTop: `1px solid ${GT.line}`, paddingTop: 12, display: 'grid', gap: 8 }}>
      <button type="button" data-testid={`credit-lox-toggle-${c.id}`} aria-expanded={open} onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', padding: 0, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: `500 14px ${GT.font}`, color: GT.ink }}>
        <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <span data-testid={`credit-lox-title-${c.id}`} style={{ fontWeight: 700 }}>{c.staff_title || c.title || 'Letter of explanation'}</span>
          <span data-testid={`credit-lox-meta-${c.id}`} style={{ fontSize: 12 }}>{loxMetaLine(c)}</span>
        </span>
        <span data-testid={`credit-lox-pill-${c.id}`} data-status={status} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 10px', borderRadius: 13, fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', whiteSpace: 'nowrap', background: pill.bg, color: pill.fg, flexShrink: 0 }}>{pill.text}</span>
      </button>
      {open ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {status === 'sent_back' && c.revision_request ? (
            <div style={{ fontSize: 13 }}><b>Sent back:</b> {c.revision_request}</div>
          ) : null}
          {isLetter && (status === 'answered' || status === 'accepted') && Array.isArray(c.guided_answers) && c.guided_answers.length ? (
            <div data-testid={`credit-lox-answers-${c.id}`} style={{ display: 'grid', gap: 6, fontSize: 13 }}>
              {c.guided_answers.map((x) => (
                <div key={x.question}><div style={{ fontWeight: 700 }}>{x.question}</div><div style={{ whiteSpace: 'pre-wrap' }}>{x.answer}</div></div>
              ))}
            </div>
          ) : isLetter && (status === 'answered' || status === 'accepted') && c.borrower_response ? (
            <div data-testid={`credit-lox-letter-${c.id}`} style={{ fontSize: 14, lineHeight: 1.6, background: GT.ground, borderRadius: 8, padding: '10px 12px', whiteSpace: 'pre-wrap' }}>{c.borrower_response}</div>
          ) : null}
          {isLetter && (status === 'answered' || status === 'accepted') ? <LoxReviewNote c={c} borrowerId={borrowerId} headers={headers} api={api} onChange={onChange} actions={false} /> : null}
          <CompFactorPanel condition={c} borrowerId={borrowerId} headers={headers} api={api} onChange={onChange} />
          {revising ? <RevisionForm c={c} missing={missing} borrowerId={borrowerId} headers={headers} api={api} onChange={onChange} onClose={() => setRevising(false)} /> : null}
          {(canAccept || canRevise) && !revising ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {canRevise ? (
                <button type="button" data-testid={`lox-request-revision-${c.id}`} disabled={busy} onClick={() => setRevising(true)} style={GBTN()}>Request a revised letter</button>
              ) : null}
              {canAccept ? (
                <button type="button" data-testid={`credit-lox-accept-${c.id}`} disabled={busy} onClick={accept} style={GBTN('primary', busy)}>
                  {busy ? 'Working…' : isLetter ? 'Accept letter' : 'Accept'}
                </button>
              ) : null}
            </div>
          ) : null}
          {error ? <div role="alert" style={{ fontSize: 13, fontWeight: 700 }}>{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export function LoxReviewList({ loxConditions, borrowerId, headers, api, onChange }) {
  // One row per item: every letter and factor item still on the file (not dismissed or rejected), accepted ones included.
  const live = (loxConditions || []).filter((c) => !['dismissed', 'rejected'].includes(String(c.status || '')) && !c.dismissed);
  if (!live.length) return null;
  return (
    <div data-testid="credit-lox-list" style={{ ...GCARD({ marginTop: 14 }), fontFamily: GT.font, color: GT.ink }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Letters of explanation · {live.length}</h2>
      {live.map((c) => <LoxRow key={c.id} c={c} borrowerId={borrowerId} headers={headers} api={api} onChange={onChange} />)}
    </div>
  );
}

function CreditReviewCard({ user, reviewSections, headers, API, id, onSectionUpdated, loxConditions }) {
  const [mode, setMode] = useState('idle');
  const [flagNote, setFlagNote] = useState('');
  const [error, setError] = useState('');
  // Liabilities + REO state. Fetched on mount from
  // GET /api/admin/borrowers/:id/liabilities; toggling exclusion fires
  // PATCH /api/admin/borrowers/:id/liabilities/:liabId which also
  // recomputes users.total_monthly_debt server-side via updateUserTotals
  // — same path the Financials tab uses, so admin override flows through
  // to Atlas pricing automatically.
  const [liabilities, setLiabilities] = useState([]);
  const [liabSummary, setLiabSummary] = useState({ totalMonthlyPayments: 0, totalBalance: 0, count: 0 });
  const [liabLoading, setLiabLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const fetchLiabilities = async () => {
    setLiabLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/liabilities`, { headers });
      setLiabilities(r.data?.liabilities || []);
      setLiabSummary(r.data?.summary || { totalMonthlyPayments: 0, totalBalance: 0, count: 0 });
    } catch (_) {
      // Non-fatal — the section can still approve/flag, just without
      // the REO + liabilities breakdown.
    } finally {
      setLiabLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLiabilities(); }, [id]);

  const toggleLiability = async (liab) => {
    setTogglingId(liab.id);
    try {
      await axios.patch(
        `${API}/api/admin/borrowers/${id}/liabilities/${liab.id}`,
        { exclude_from_dti: !liab.exclude_from_dti },
        { headers }
      );
      await fetchLiabilities();
      // Refresh parent so users.total_monthly_debt + Atlas re-price
      // surface on the Financials tab too. Same hook the Income /
      // Assets override paths trigger.
      if (onSectionUpdated) onSectionUpdated('credit', null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not update liability.');
    } finally {
      setTogglingId(null);
    }
  };

  // Split tradelines into REO (mortgage-typed) vs everything else.
  // The credit report's `account_type` arrives as 'mortgage' for any
  // mortgage liability; case-fold to be safe.
  const mortgageLiabs = liabilities.filter(l => String(l.account_type || '').toLowerCase() === 'mortgage');
  const nonMortgageLiabs = liabilities.filter(l => String(l.account_type || '').toLowerCase() !== 'mortgage');

  // Existing-mortgage block on the user row (admin-curated, surfaces
  // the borrower's current primary-residence mortgage with disposition
  // metadata — 'selling' or 'keeping' — that the credit-report
  // tradelines don't carry).
  const hasUserMortgage = !!(user?.existing_mortgage_detected
                           || user?.existing_mortgage_lender
                           || user?.existing_mortgage_balance
                           || user?.existing_mortgage_payment);
  const userMortgageExcluded = !!user?.existing_mortgage_excluded_from_dti;
  const dispositionRaw = user?.existing_mortgage_disposition || null;
  const isSelling = dispositionRaw === 'selling';
  const isKeeping = dispositionRaw === 'keeping';

  // Setting the disposition POSTs to the existing mortgage-disposition
  // endpoint, which both flips users.existing_mortgage_excluded_from_dti
  // accordingly AND (when disposition='selling') auto-creates a
  // payoff_required additional_needs row titled "Proof of Sale / Payoff
  // Required" so UW can't sign off until it's resolved. Switching back
  // to 'keeping' resolves any pending payoff_required need.
  const [settingDisposition, setSettingDisposition] = useState(false);
  const setDisposition = async (d) => {
    setSettingDisposition(true);
    setError('');
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${id}/mortgage-disposition`,
        { disposition: d, notes: 'Set from Credit Review card' },
        { headers }
      );
      // Refresh parent so the new disposition + the linked payoff_required
      // need + the updated DTI total all surface immediately.
      if (onSectionUpdated) onSectionUpdated('credit', null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not set disposition.');
    } finally {
      setSettingDisposition(false);
    }
  };

  // Tri-bureau scores. Xactus stores them per-bureau; the qualifying
  // FICO is the MIDDLE of the three (Fannie/Freddie convention) and is
  // mirrored to users.fico_score_actual at pull time.
  const eq = Number(user?.xactus_equifax_score)   || null;
  const ex = Number(user?.xactus_experian_score)  || null;
  const tu = Number(user?.xactus_transunion_score) || null;
  const fico = Number(user?.fico_score_actual) || Number(user?.credit_score) || null;
  // 'success' was one of two spellings tested here and it is written by nothing —
  // a dead branch that read as a handled case. hasReport() maps the real values
  // and the drifted ones together.
  const pulled = hasReport(user?.xactus_pull_status)
              || !!(eq || ex || tu || fico);
  const pulledAt = user?.fico_pulled_at || null;

  const tradelines  = Number(user?.xactus_tradeline_count)   || 0;
  const collections = Number(user?.xactus_collections_count) || 0;
  const bankruptcy  = !!user?.xactus_bankruptcy_flag;
  const foreclosure = !!user?.xactus_foreclosure_flag;

  // Derog flag array — pg returns jsonb either as a string or an array
  // depending on the driver path. Normalize.
  const derogFlags = useMemo(() => {
    const raw = user?.xactus_derog_flags;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }, [user?.xactus_derog_flags]);

  const reviewSectionRow = (reviewSections || []).find(s => s.section === 'credit');
  const accepted = ['accepted','approved'].includes(reviewSectionRow?.status || '');
  const flagged  = reviewSectionRow?.status === 'flagged';

  const handleApprove = async () => {
    setMode('submitting'); setError('');
    try {
      await axios.post(`${API}/api/review/admin/${id}/section`,
        { section: 'credit', action: 'accept' }, { headers });
      if (onSectionUpdated) onSectionUpdated('credit', 'accepted');
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not approve.');
      setMode('idle');
    }
  };

  const handleSubmitFlag = async () => {
    if (!flagNote.trim()) return;
    setMode('submitting'); setError('');
    try {
      await axios.post(`${API}/api/review/admin/${id}/section`,
        { section: 'credit', action: 'flag', note: flagNote.trim() }, { headers });
      if (onSectionUpdated) onSectionUpdated('credit', 'flagged');
      setMode('idle'); setFlagNote('');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not flag.');
      setMode('idle');
    }
  };

  // FICO band coloring for the headline score.
  const ficoBand = (n) => {
    if (n == null) return { bg: '#f3f4f6', color: '#6b7280', label: '—' };
    if (n >= 740)  return { bg: '#f0fde4', color: '#3d7a00', label: 'Excellent' };
    if (n >= 670)  return { bg: '#eff6ff', color: '#1e40af', label: 'Good' };
    if (n >= 580)  return { bg: '#fef3c7', color: '#92400e', label: 'Fair' };
    return                  { bg: '#fee2e2', color: '#991b1b', label: 'Sub-580' };
  };
  const band = ficoBand(fico);

  return (
    <div style={{ marginBottom: 18, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
        Credit Review
        {accepted && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, background: '#f0fde4', color: '#3d7a00', border: '1px solid #acf44e', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Approved
          </span>
        )}
        {flagged && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Flagged
          </span>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>
        {!pulled ? (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#7a5200', lineHeight: 1.6, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>No credit pull on file yet</div>
            The credit check hasn't run for this borrower. Once the borrower authorizes the credit pull (step 7) — or the demo seed populates xactus_* columns — the qualifying FICO, tri-bureau breakdown, and tradeline summary will populate here.
          </div>
        ) : (
          <>
            {/* Qualifying FICO — headline. Mirrors Income's "qualifying
                monthly" + Assets' "qualifying liquid assets" treatment. */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>
                  Qualifying mid FICO
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
                  {fico != null ? fico : '—'}
                </div>
              </div>
              {fico != null && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 9999,
                  background: band.bg, color: band.color,
                  textTransform: 'uppercase', letterSpacing: 0.3,
                  alignSelf: 'flex-end', marginBottom: 4,
                }}>
                  {band.label}
                </span>
              )}
              {pulledAt && (
                <div style={{ fontSize: 11, color: '#9ca3af', alignSelf: 'flex-end', marginBottom: 4 }}>
                  Pulled {new Date(pulledAt).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Tri-bureau breakdown. Same per-source breakdown style as
                AssetsCalculationCard for visual parity across steps. */}
            <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, fontWeight: 700 }}>
                Tri-bureau scores
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Equifax</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{eq != null ? eq : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Experian</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{ex != null ? ex : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>TransUnion</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{tu != null ? tu : '—'}</div>
                </div>
              </div>
            </div>

            {/* Account + derog summary. Counts surface above the
                detailed derog list — admin glances at the counts first,
                expands into the list if anything's non-zero. */}
            <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, fontWeight: 700 }}>
                Account summary
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: derogFlags.length > 0 ? 10 : 0 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Tradelines</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{tradelines}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Collections</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: collections > 0 ? '#991b1b' : '#374151' }}>{collections}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Bankruptcy</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: bankruptcy ? '#991b1b' : '#374151' }}>{bankruptcy ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Foreclosure</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: foreclosure ? '#991b1b' : '#374151' }}>{foreclosure ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Per-derog detail. Each flag has a `type` (recent_inquiry,
                  collection, late, charge_off, etc.) plus an optional
                  creditor + detail + date. Renders as a bullet list so
                  admin can scan and decide whether any need an LOX. */}
              {derogFlags.length > 0 && (
                <div style={{ paddingTop: 10, borderTop: '1px solid #eef0f2' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, fontWeight: 700 }}>
                    Derogatory items
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {derogFlags.map((f, i) => {
                      // 🔑 f.severity is attached server-side from the shared derogSeverity (routes/admin.js borrower
                      // detail). BLOCKING → red; everything else, including a missing or unknown value, reads as
                      // SIGNIFICANT (amber) — the helper's safe default, never a mild grey. statusColors only.
                      const sev = String(f?.severity || '').toLowerCase();
                      const dot = sev === 'blocking' ? STATUS_COLORS.on_hold.bg : STATUS_COLORS.adverse_pending.bg;
                      const sevLabel = sev === 'blocking' ? 'blocking' : 'significant';
                      const typeLabel = String(f?.type || 'derog').replace(/_/g, ' ');
                      const creditor = f?.creditor ? ` — ${f.creditor}` : '';
                      const detail   = f?.detail ? ` (${f.detail})` : '';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                          <span data-testid="derog-severity-dot" data-severity={sevLabel} title={sevLabel} style={{ width: 6, height: 6, borderRadius: 9999, background: dot, flexShrink: 0 }} />
                          <span><span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{typeLabel}</span>{creditor}{detail}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* REO — Real Estate Owned. Surfaces the borrower's current
                primary-residence mortgage (admin-curated, with
                disposition metadata that the credit-report tradelines
                don't carry) AND any mortgage-typed tradelines from the
                pull. Each row exposes its DTI exclusion state inline. */}
            {(hasUserMortgage || mortgageLiabs.length > 0) && (
              <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8, fontWeight: 700 }}>
                  REO (Real Estate Owned)
                </div>

                {hasUserMortgage && (
                  <div style={{
                    background: userMortgageExcluded ? '#f9fafb' : '#fff',
                    border: '1px solid #e5e7eb', borderRadius: 6,
                    padding: '10px 12px', marginBottom: mortgageLiabs.length > 0 ? 8 : 0,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#111827' }}>
                          {user.existing_mortgage_lender || 'Existing mortgage'}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          {user.existing_mortgage_balance != null && <>Balance: ${Math.round(Number(user.existing_mortgage_balance)).toLocaleString()}</>}
                          {user.existing_mortgage_payment != null && <> · ${Math.round(Number(user.existing_mortgage_payment)).toLocaleString()}/mo</>}
                        </div>
                        {user.existing_mortgage_notes && (
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, fontStyle: 'italic' }}>
                            {user.existing_mortgage_notes}
                          </div>
                        )}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999,
                        background: userMortgageExcluded ? '#f3f4f6' : '#f0fde4',
                        color: userMortgageExcluded ? '#6b7280' : '#3d7a00',
                        border: `1px solid ${userMortgageExcluded ? '#e5e7eb' : '#acf44e'}`,
                        textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
                      }}>
                        {userMortgageExcluded ? 'Excluded from DTI' : 'Included in DTI'}
                      </span>
                    </div>

                    {/* Selling vs Keeping — borrower's response. When
                        'selling', the mortgage is auto-excluded AND the
                        backend creates a payoff_required additional_need
                        ("Proof of Sale / Payoff Required") so UW can't
                        sign off without proof. When 'keeping', the
                        mortgage is included in DTI and any pending
                        payoff need auto-resolves. Pending (null)
                        prompts admin to set it inline. */}
                    <div style={{ paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, fontWeight: 700 }}>
                        Borrower's response — selling or keeping this property?
                      </div>
                      {dispositionRaw == null ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: '#92400e', fontStyle: 'italic' }}>Not yet answered.</span>
                          {['keeping'].map(d => (
                            <button
                              key={d}
                              type="button"
                              onClick={() => setDisposition(d)}
                              disabled={settingDisposition}
                              style={{
                                padding: '4px 12px',
                                background: '#fff',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 11, fontWeight: 700, color: '#374151',
                                textTransform: 'capitalize',
                                cursor: settingDisposition ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Mark as {d}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 12, color: '#111827', marginBottom: 6 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{dispositionRaw}</strong>
                            {isSelling && ' — the payment stays in DTI until the executed sale contract and the cleared financing contingency are on file.'}
                            {isKeeping && ' — mortgage included in DTI.'}
                          </div>
                          {isSelling && (
                            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', fontSize: 11, color: '#92400e', marginBottom: 6 }}>
                              An additional need <strong>"Proof of Sale / Payoff Required"</strong> has been added to the borrower's queue. UW review can't complete until the borrower uploads a listing agreement, purchase contract, or payoff letter.
                            </div>
                          )}
                          {isSelling && (
                            <button
                              type="button"
                              onClick={() => setDisposition('keeping')}
                              disabled={settingDisposition}
                              style={{
                                padding: '4px 12px',
                                background: '#fff',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 11, fontWeight: 700, color: '#374151',
                                cursor: settingDisposition ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Change to keeping
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {mortgageLiabs.map(l => {
                  const monthly = Number(l.monthly_payment) || 0;
                  const balance = Number(l.outstanding_balance) || 0;
                  const excluded = !!l.exclude_from_dti;
                  return (
                    <div key={l.id} style={{
                      background: excluded ? '#f9fafb' : '#fff',
                      border: '1px solid #e5e7eb', borderRadius: 6,
                      padding: '8px 10px', marginBottom: 6,
                      opacity: excluded ? 0.7 : 1,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>{l.creditor_name || 'Mortgage'}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                            Balance: ${balance.toLocaleString()} · ${monthly.toLocaleString()}/mo
                            {l.payment_status && l.payment_status !== 'current' && <> · <span style={{ color: '#991b1b', fontWeight: 600, textTransform: 'capitalize' }}>{String(l.payment_status).replace(/_/g, ' ')}</span></>}
                          </div>
                          {excluded && l.exclude_reason && (
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, fontStyle: 'italic' }}>
                              Auto-excluded: {l.exclude_reason}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleLiability(l)}
                          disabled={togglingId === l.id}
                          style={{
                            padding: '4px 10px', fontSize: 11, fontWeight: 700,
                            background: excluded ? '#3a6ad9' : '#fff',
                            color: excluded ? '#fff' : '#374151',
                            border: excluded ? 'none' : '1px solid #d1d5db',
                            borderRadius: 6, cursor: togglingId === l.id ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {togglingId === l.id ? '…' : excluded ? 'Include in DTI' : 'Exclude'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Liabilities — everything non-mortgage. Each row toggles
                its DTI inclusion via the existing PATCH endpoint which
                also recomputes users.total_monthly_debt server-side
                (same path the Financials tab + Atlas reprice consume).
                Auto-excluded items (exclude_from_dti=true with an
                exclude_reason) render in a muted state with the reason
                inline — admin can flip them back on with one click. */}
            <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>
                  Liabilities
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  Total in DTI: <strong style={{ color: '#111827' }}>${Math.round(liabSummary.totalMonthlyPayments).toLocaleString()}/mo</strong>
                </div>
              </div>
              {liabLoading && nonMortgageLiabs.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Loading liabilities…</div>
              ) : nonMortgageLiabs.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No non-mortgage liabilities on file.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {nonMortgageLiabs.map(l => {
                    const monthly = Number(l.monthly_payment) || 0;
                    const balance = Number(l.outstanding_balance) || 0;
                    const excluded = !!l.exclude_from_dti;
                    const typeLabel = String(l.account_type || 'other').replace(/_/g, ' ');
                    return (
                      <div key={l.id} style={{
                        background: excluded ? '#f9fafb' : '#fff',
                        border: '1px solid #e5e7eb', borderRadius: 6,
                        padding: '8px 10px',
                        opacity: excluded ? 0.7 : 1,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#111827' }}>
                              {l.creditor_name || 'Unnamed creditor'}
                              <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af', marginLeft: 8, textTransform: 'capitalize' }}>{typeLabel}</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              ${monthly.toLocaleString()}/mo
                              {balance > 0 && <> · Balance ${balance.toLocaleString()}</>}
                              {l.payment_status && l.payment_status !== 'current' && <> · <span style={{ color: '#991b1b', fontWeight: 600, textTransform: 'capitalize' }}>{String(l.payment_status).replace(/_/g, ' ')}</span></>}
                            </div>
                            {excluded && l.exclude_reason && (
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, fontStyle: 'italic' }}>
                                Auto-excluded: {l.exclude_reason}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleLiability(l)}
                            disabled={togglingId === l.id}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 700,
                              background: excluded ? '#3a6ad9' : '#fff',
                              color: excluded ? '#fff' : '#374151',
                              border: excluded ? 'none' : '1px solid #d1d5db',
                              borderRadius: 6, cursor: togglingId === l.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {togglingId === l.id ? '…' : excluded ? 'Include in DTI' : 'Exclude'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#991b1b', marginBottom: 10 }}>
            {error}
          </div>
        )}

        {mode === 'idle' && !accepted && !flagged && (
          <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
            <button
              type="button"
              onClick={handleApprove}
              style={{ flex: 1, padding: 10, background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
            >
              Approve credit
            </button>
            <button
              type="button"
              onClick={() => { setMode('flag'); setFlagNote(''); }}
              style={{ padding: '10px 16px', background: '#fff', color: '#ff082f', border: '1px solid #ff082f', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
            >
              Flag
            </button>
          </div>
        )}

        {mode === 'flag' && (
          <div style={{ paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Flag reason (required — audit trail)
            </label>
            <textarea
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              rows={3}
              placeholder="What's the concern with credit? (e.g. unresolved derogs, late payments, FICO below program floor)"
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 10, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSubmitFlag}
                disabled={!flagNote.trim()}
                style={{ flex: 1, padding: 10, background: flagNote.trim() ? '#ff082f' : '#e5e7eb', color: flagNote.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8, cursor: flagNote.trim() ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 13 }}
              >
                Submit flag
              </button>
              <button
                type="button"
                onClick={() => { setMode('idle'); setFlagNote(''); setError(''); }}
                style={{ padding: '10px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'submitting' && (
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', textAlign: 'center', paddingTop: 8 }}>
            Saving…
          </div>
        )}
        <LoxReviewList loxConditions={loxConditions} borrowerId={id} headers={headers} api={API} onChange={onSectionUpdated} />
      </div>
    </div>
  );
}

// Document-keyword maps for SectionAIReviewCard's preview picker. Same
// keyword sets DOC_CATEGORIES uses for filterDocsForCategory, but
// scoped to the doc types that make sense as the "primary doc" the
// card surfaces a preview of. Mirrors DOC_CATEGORIES.{housing,credit,misc}
// so the per-step preview lines up with the per-step extraction filter.
const SECTION_PREVIEW_DOC_KEYWORDS = {
  identity: ['drivers_license', 'passport', 'government_id', 'photo_id', 'identity', 'id_document', 'state_id', 'dl', 'license'],
  housing: ['vor', 'vom', 'verification_of_rent', 'verification_of_mortgage', 'mortgage_statement', 'rental_history', 'landlord_letter'],
  credit:  ['credit_report'],
  misc:    ['bankruptcy_lox', 'foreclosure_lox', 'employment_gap_lox', 'mortgage_default_lox', 'misc_req_'],
};

// PrivateMortgageVomCard — Housing step. Surfaces the private_mortgage_vom
// need (owner with no mortgage tradeline) + the borrower's attestation/lender
// contact, and lets the underwriter request a VOM directly from the lender
// (POST /borrowers/:id/request-vom → tokenized link → routes/vom.js). The
// Housing section accept is blocked server-side until this need resolves.
function PrivateMortgageVomCard({ additionalNeeds, id, headers, API, onSectionUpdated }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr]   = useState('');
  // Inline lender contact — for files where the borrower supplied it on Step 3
  // the backend uses the stored value (leave blank); for legacy/backfilled
  // files with nothing on file, the underwriter enters it here. Sent in the
  // request body; backend falls back to the stored contact when blank.
  const [lenderName, setLenderName]   = useState('');
  const [lenderEmail, setLenderEmail] = useState('');
  const [lenderPhone, setLenderPhone] = useState('');
  const need = (additionalNeeds || []).find(n => n.category === 'private_mortgage_vom');
  if (!need) return null;
  const resolved = !isOpenNeed(need);
  const request = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/request-vom`, {
        lender_name: lenderName.trim() || undefined,
        lender_email: lenderEmail.trim() || undefined,
        property_address: undefined,
      }, { headers });
      setSent(true);
      if (onSectionUpdated) onSectionUpdated();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not send the VOM request.');
    } finally { setBusy(false); }
  };
  const inStyle = { fontSize: 11.5, padding: '6px 8px', borderRadius: 6, border: '1px solid #fed7aa', background: '#fff' };
  return (
    <div style={{
      marginBottom: 14, padding: '14px 16px',
      background: resolved ? '#f0fdf4' : '#fffaf0',
      border: `1px solid ${resolved ? '#bbf7d0' : '#fed7aa'}`,
      borderLeft: `3px solid ${resolved ? '#16a34a' : '#ea580c'}`, borderRadius: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: resolved ? '#166534' : '#9a3412', marginBottom: 4 }}>
        {resolved ? '✓ Verification of Mortgage received' : 'Private Mortgage — Verification of Mortgage required'}
      </div>
      <div style={{ fontSize: 11.5, color: '#7c2d12', lineHeight: 1.55, marginBottom: 8 }}>
        Owner with no mortgage tradeline on the credit report. Request a VOM directly from the lender/servicer to establish the 12-month 0x30x12 housing history. Housing can't be accepted until this is resolved.
      </div>
      {need.borrower_response && (
        <div style={{ fontSize: 11.5, color: '#374151', background: '#fff', border: '1px solid #fed7aa', borderRadius: 6, padding: '8px 10px', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
          {need.borrower_response}
        </div>
      )}
      {!resolved && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
            <input style={inStyle} value={lenderName}  onChange={e => setLenderName(e.target.value)}  placeholder="Lender / servicer name" />
            <input style={inStyle} value={lenderEmail} onChange={e => setLenderEmail(e.target.value)} placeholder="Lender email" />
            <input style={{ ...inStyle, gridColumn: '1 / -1' }} value={lenderPhone} onChange={e => setLenderPhone(e.target.value)} placeholder="Lender phone (optional)" />
          </div>
          <div style={{ fontSize: 10, color: '#9a3412', marginBottom: 8 }}>
            Leave blank to use the contact the borrower provided in the application.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" disabled={busy || sent} onClick={request}
              style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', background: busy || sent ? '#e5e7eb' : '#3a6ad9', color: busy || sent ? '#9ca3af' : '#fff', border: 'none', borderRadius: 7, cursor: busy || sent ? 'default' : 'pointer' }}>
              {busy ? 'Sending…' : sent ? '✓ Request sent to lender' : 'Request VOM from Lender →'}
            </button>
            {err && <span style={{ fontSize: 11, color: '#991b1b' }}>{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// SectionAIReviewCard — generic Step 4 / 5 / 6 (Housing / Credit / Misc)
// surface that replaces the legacy SectionReviewPanel on those steps.
// Mirrors the IncomeCalculationCard / AssetsCalculationCard shape so
// the wizard reads consistently from Step 2 onward.
//
// Housing / Credit / Misc don't have a $ figure to override the way
// Income + Assets do; instead the card surfaces the AI's section notes
// (status + narrative + items) alongside an embedded preview of the
// primary document for that section. Terminal actions are Approve or
// Flag.
//
// Props:
//   sectionKey  — 'housing' | 'credit' | 'misc' (matches review_sections.section + analysis_json sections[].category)
//   sectionLabel — display string ('Housing Review' / 'Credit Review' / 'Miscellaneous Review')
//   approveLabel — button copy ('Approve housing' / 'Approve credit' / 'Approve miscellaneous')
//   flagPrompt   — textarea placeholder copy specific to the section
//   missingCopy  — fallback when there's no section finding from the AI
function SectionAIReviewCard({ sectionKey, sectionLabel, approveLabel, flagPrompt, missingCopy, user, documents, fullAnalysis, reviewSections, headers, API, id, onSectionUpdated }) {
  const [mode, setMode]       = useState('idle'); // 'idle' | 'flag' | 'submitting'
  const [flagNote, setFlagNote] = useState('');
  const [error, setError]     = useState('');
  const [pdfOpen, setPdfOpen] = useState(true);
  const [pdfUrl, setPdfUrl]   = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError]     = useState('');

  // Section AI finding — block from analysis_json.sections[] where
  // category matches this card's sectionKey. Carries status (good /
  // needs_attention / missing), message (narrative), and items[]
  // (per-doc verdicts).
  const sectionFinding = useMemo(() => {
    const sections = Array.isArray(fullAnalysis?.sections) ? fullAnalysis.sections : [];
    return sections.find(s => s.category === sectionKey) || null;
  }, [fullAnalysis, sectionKey]);

  const reviewSectionRow = (reviewSections || []).find(s => s.section === sectionKey);
  const accepted = ['accepted','approved'].includes(reviewSectionRow?.status || '');
  const flagged  = reviewSectionRow?.status === 'flagged';
  // Credit lock retired (backend commit 570465d). The banner that used
  // to surface "Credit section is locked. Accept all other sections
  // first." was removed when the gate was retired — credit can now be
  // approved independently of the other sections, same as every other
  // step.

  // Pick the primary section doc for the embedded preview. Most recent
  // doc matching this section's keyword set wins. Each ExtractionCard
  // above already embeds its own PDF for docs WITH an AI extraction, so
  // this preview slot is the fallback for borrower-uploaded section
  // docs that haven't been processed yet (or doc types like LOX that
  // don't get AI extraction at all).
  const previewDoc = useMemo(() => {
    const docKeywords = SECTION_PREVIEW_DOC_KEYWORDS[sectionKey] || [];
    if (docKeywords.length === 0) return null;
    const candidates = (documents || []).filter(d => {
      const t = (d.document_type || '').toLowerCase();
      return docKeywords.some(k => t.includes(k));
    });
    if (candidates.length === 0) return null;
    // Latest by upload_date; falls back to id order if upload_date is missing.
    return candidates.sort((a, b) =>
      new Date(b.upload_date || 0) - new Date(a.upload_date || 0)
    )[0];
  }, [documents, sectionKey]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Auto-load the preview when the card mounts AND we actually have a
  // doc to preview. Skipped when previewDoc is null (no housing docs
  // on file).
  useEffect(() => {
    if (!previewDoc) return;
    let cancelled = false;
    setPdfUrl(null);
    setPdfError('');
    setPdfLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/borrowers/${id}/documents/${previewDoc.id}/view`,
          { headers, responseType: 'blob' }
        );
        if (cancelled) return;
        setPdfUrl(URL.createObjectURL(r.data));
      } catch (e) {
        if (cancelled) return;
        setPdfError(e.response?.data?.error || e.message || 'Could not load preview.');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Re-fetch ONLY when the previewed doc (or borrower) changes. Depending on
    // pdfUrl / pdfLoading / headers made the effect cancel its own in-flight
    // fetch on the setPdfLoading(true) re-render, so pdfUrl/pdfError never got
    // set and the preview stuck on "Loading…" forever (surfaced on the identity
    // step when the ID file was missing/unreadable — the 404 error never showed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDoc?.id, id]);

  const handleApprove = async () => {
    setMode('submitting');
    setError('');
    try {
      await axios.post(`${API}/api/review/admin/${id}/section`,
        { section: sectionKey, action: 'accept' },
        { headers }
      );
      if (onSectionUpdated) onSectionUpdated(sectionKey, 'accepted');
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not approve.');
      setMode('idle');
    }
  };

  const handleSubmitFlag = async () => {
    if (!flagNote.trim()) return;
    setMode('submitting');
    setError('');
    try {
      await axios.post(`${API}/api/review/admin/${id}/section`,
        { section: sectionKey, action: 'flag', note: flagNote.trim() },
        { headers }
      );
      if (onSectionUpdated) onSectionUpdated(sectionKey, 'flagged');
      setMode('idle');
      setFlagNote('');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not flag.');
      setMode('idle');
    }
  };

  // AI status badge palette — same encoding the SectionReviewPanel
  // used so admins reading both surfaces see the same colors.
  const statusBadge = (() => {
    const s = sectionFinding?.status;
    if (s === 'good')            return { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: 'AI: GOOD' };
    if (s === 'needs_attention') return { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: 'AI: NEEDS ATTENTION' };
    if (s === 'missing')         return { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: 'AI: MISSING' };
    return null;
  })();

  return (
    <div style={{ marginBottom: 18, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
        {sectionLabel}
        {accepted && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999,
            background: '#f0fde4', color: '#3d7a00', border: '1px solid #acf44e',
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            Approved
          </span>
        )}
        {flagged && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999,
            background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            Flagged
          </span>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>
        {/* AI notes block — the section finding from analysis_json.
            Mirrors the "How was this calculated" block on the Income card. */}
        {sectionFinding ? (
          <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>
                AI notes
              </div>
              {statusBadge && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 9999,
                  background: statusBadge.bg, border: `1px solid ${statusBadge.border}`,
                  color: statusBadge.color,
                  textTransform: 'uppercase', letterSpacing: 0.3,
                }}>{statusBadge.label}</span>
              )}
            </div>
            {sectionFinding.message && (
              <div style={{ marginBottom: 8 }}>{sectionFinding.message}</div>
            )}
            {Array.isArray(sectionFinding.items) && sectionFinding.items.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6, paddingTop: 6, borderTop: '1px solid #eef0f2' }}>
                {sectionFinding.items.map((it, i) => {
                  const itStatus = String(it?.status || '').toLowerCase();
                  const dot = itStatus === 'good'    ? '#acf44e'
                            : itStatus === 'warning' ? '#f59e0b'
                            : itStatus === 'concern' || itStatus === 'flag' ? '#ff082f'
                            : itStatus === 'missing' ? '#ff082f'
                            : '#9ca3af';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: 9999, background: dot, flexShrink: 0 }} />
                      <span>{it?.label || it?.docType || 'Item'}{it?.note ? ` — ${it.note}` : ''}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#7a5200', lineHeight: 1.6, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{missingCopy?.title || 'No analysis on file yet'}</div>
            {missingCopy?.body || "The document check hasn't produced a section finding. Once the AI run completes, the AI's notes and per-doc verdicts will populate here."}
          </div>
        )}

        {/* Embedded preview of the primary housing doc. Collapsible so
            admin can hide it for steps where the extraction cards
            above already provided what they needed. */}
        {previewDoc && (
          <div style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setPdfOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: '#f9fafb',
                border: 'none', borderBottom: pdfOpen ? '1px solid #e5e7eb' : 'none',
                cursor: 'pointer', textAlign: 'left',
                fontSize: 12, fontWeight: 600, color: '#374151',
              }}
            >
              <span style={{ flex: 1 }}>
                Document preview — {previewDoc.original_name || previewDoc.filename || previewDoc.document_type}
              </span>
              {pdfLoading && <span style={{ fontSize: 11, color: '#9ca3af' }}>Loading…</span>}
              <span style={{ fontSize: 11, color: '#6b7280' }}>{pdfOpen ? '▲' : '▼'}</span>
            </button>
            {pdfOpen && (
              <div style={{ background: '#fff' }}>
                {pdfError ? (
                  <div style={{ padding: 12, fontSize: 12, color: '#991b1b', background: '#fee2e2' }}>
                    {pdfError}
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    title={`${sectionLabel} doc ${previewDoc.id}`}
                    src={pdfUrl}
                    style={{ width: '100%', height: 520, border: 'none', display: 'block' }}
                  />
                ) : (
                  <div style={{ padding: 12, fontSize: 12, color: '#6b7280' }}>Loading PDF…</div>
                )}
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#991b1b', marginBottom: 10 }}>
            {error}
          </div>
        )}

        {mode === 'idle' && !accepted && !flagged && (
          <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
            <button
              type="button"
              onClick={handleApprove}
              style={{
                flex: 1, padding: 10,
                background: '#acf44e', color: '#1a1a1a',
                border: 'none', borderRadius: 8,
                cursor: 'pointer', fontWeight: 700, fontSize: 13,
              }}
            >
              {approveLabel}
            </button>
            <button
              type="button"
              onClick={() => { setMode('flag'); setFlagNote(''); }}
              style={{
                padding: '10px 16px',
                background: '#fff', color: '#ff082f',
                border: '1px solid #ff082f', borderRadius: 8,
                cursor: 'pointer', fontWeight: 700, fontSize: 13,
              }}
            >
              Flag
            </button>
          </div>
        )}

        {mode === 'flag' && (
          <div style={{ paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Flag reason (required — audit trail)
            </label>
            <textarea
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              rows={3}
              placeholder={flagPrompt || 'What\'s the concern with this section?'}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 13,
                border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 10,
                boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSubmitFlag}
                disabled={!flagNote.trim()}
                style={{
                  flex: 1, padding: 10,
                  background: flagNote.trim() ? '#ff082f' : '#e5e7eb',
                  color: flagNote.trim() ? '#fff' : '#9ca3af',
                  border: 'none', borderRadius: 8,
                  cursor: flagNote.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: 700, fontSize: 13,
                }}
              >
                Submit flag
              </button>
              <button
                type="button"
                onClick={() => { setMode('idle'); setFlagNote(''); setError(''); }}
                style={{
                  padding: '10px 16px',
                  background: '#fff', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 8,
                  cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'submitting' && (
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', textAlign: 'center', paddingTop: 8 }}>
            Saving…
          </div>
        )}
      </div>
    </div>
  );
}

// IdentityVerificationPanel — Step 1 (Identity) replacement for the
// generic AIExtractionsPanel. Shows the identity verification source
// (Persona vs admin-manual) and auto-accepts the identity review
// section when Persona has completed. Mirrors the pattern the
// borrower-side ReadyCheck identity section uses (Verification.js
// reads identity_verified || verified_manually) so the underwriting
// review surface stays consistent with what the borrower saw.
//
// Auto-approve semantics: when idVerification.status === 'approved'
// AND verified_manually === 0 (Persona path), POST the section accept
// once on mount. Uses a ref to prevent re-fires across re-renders.
// If the user manually verified through the admin tool (verified_manually = 1),
// surface the manual badge + the admin name on the audit line — no
// auto-accept since admin already made the call explicitly.
function IdentityVerificationPanel({ user, coBorrower, idVerification, reviewSections, headers, API, id, onSectionUpdated }) {
  const autoApprovedRef = useRef(false);
  const idStatus      = idVerification?.status || null;
  const verifiedManually = Number(idVerification?.verified_manually) === 1;
  // Persona writes status='completed' (verified_manually=0) when the inquiry
  // passes — see routes/identity.js (and it auto-accepts the identity review
  // section there too). The status='approved' value is the ADMIN-MANUAL path
  // (always verified_manually=1). The old check (status==='approved' &&
  // !verifiedManually) was therefore never true for a real Persona pass, so a
  // completed Persona verification rendered as "No Persona attempt on file"
  // even though the section was already accepted. Recognize 'completed' (and a
  // non-manual 'approved', for forward-compat) as the Persona-approved state.
  // ⚠️ NOT a step-4 uploaded ID that the automatic check passed (verification_path 'manual_ai', 2026-10-19): that is
  // 'approved' with verified_manually 0 too, and it is not Persona.
  const isManualAi = idVerification?.verification_path === 'manual_ai';
  const isPersonaApproved = (idStatus === 'completed' || idStatus === 'approved') && !verifiedManually && !isManualAi;
  const identitySection = (reviewSections || []).find(s => s.section === 'identity');
  const sectionAccepted = ['accepted','approved'].includes(identitySection?.status || '');

  useEffect(() => {
    if (autoApprovedRef.current) return;
    if (!isPersonaApproved) return;
    if (sectionAccepted) { autoApprovedRef.current = true; return; }
    autoApprovedRef.current = true;
    (async () => {
      try {
        await axios.post(`${API}/api/review/admin/${id}/section`,
          { section: 'identity', action: 'accept', note: 'Auto-accepted via Persona' },
          { headers }
        );
        if (onSectionUpdated) onSectionUpdated('identity', 'accepted');
      } catch (e) {
        console.warn('[Review] Identity auto-accept failed (non-fatal):', e.message);
        autoApprovedRef.current = false;
      }
    })();
  }, [isPersonaApproved, sectionAccepted, API, id, headers, onSectionUpdated]);

  const verifiedAt = idVerification?.admin_verified_at
                  || idVerification?.completed_at
                  || idVerification?.updated_at
                  || null;

  const renderRow = (label, person) => {
    const isPrimary = label === 'Primary';
    // Co-borrower's identity_verifications row lives on a separate
    // record; this panel currently only surfaces the primary's. Stub
    // out the co-borrower row when no co-borrower is present.
    if (!isPrimary && !person) return null;
    if (!isPrimary) {
      return (
        <div style={{ flex: 1, minWidth: 260, padding: '14px 16px', background: '#fafbfc', border: '1px solid #e5e7eb', borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
            {`${person?.first_name || ''} ${person?.last_name || ''}`.trim() || person?.email || '—'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
            Co-borrower identity verification is reviewed alongside the primary in the borrower's own flow.
          </div>
        </div>
      );
    }
    // Verification-method indicator — how identity was verified, read
    // from users.identity_verified (0/1) + users.identity_verified_method
    // ('persona' | 'readycheck_ai' | 'admin_manual' | null). Surfaced so
    // the underwriter knows the source at a glance; ReadyCheck-AI-verified
    // files still need the underwriter to Accept the identity section below.
    const methodIndicator = (() => {
      if (!isPrimary) return null;
      if (!Number(user?.identity_verified)) return null;
      // 🔑 A flagged ID is never shown as verified (Jesse, 2026-10-19).
      if (idStatus === 'flagged') return { bg: '#fff7ed', border: '#fed7aa', color: '#9a3412', label: '🚩 Flagged — under review' };
      const m = user?.identity_verified_method || null;
      const themed = {
        persona:      { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: '✓ Verified via Persona' },
        readycheck_ai:{ bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: '✓ Verified by the automatic ID check — review the ID below' },
        admin_manual: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: '✓ Verified manually' },
        manual_ai:    { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: '✓ Verified: uploaded ID, automatic check' },
      }[m] || { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: '✓ Identity verified' };
      return themed;
    })();

    let statusPill, sourceLine;
    // 🔑 THE STEP-4 AUTOMATIC CHECK'S OUTCOMES (2026-10-19, item 13). Hold and flag first: neither ever reads as verified.
    if (idStatus === 'id_review_hold') {
      statusPill = { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: '⏸ On hold — under review' };
      sourceLine = 'The automatic ID check saw possible alteration (see the identity flag). Identity is NOT verified and the borrower cannot authorize the credit check. It is decided in the fraud review queue: dismissing the last ID flag clears the hold (the borrower is told only that they can continue).';
    } else if (idStatus === 'flagged') {
      statusPill = { bg: '#fff7ed', border: '#fed7aa', color: '#9a3412', label: '🚩 Flagged — under review' };
      sourceLine = 'The automatic ID check was not sure (see the identity flag). The borrower continued. It is decided in the fraud review queue.';
    } else if (isManualAi && idStatus === 'approved' && !verifiedManually) {
      statusPill = { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: '✓ Uploaded ID — automatic check passed' };
      sourceLine = 'The borrower uploaded their ID and the automatic check passed it. The identity section was accepted automatically.';
    } else if (idStatus === 'needs_fix') {
      statusPill = { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: '⏳ Borrower re-uploading their ID' };
      sourceLine = 'The automatic check asked the borrower to fix their ID upload (for example a blurry photo).';
    } else if (isPersonaApproved) {
      statusPill = { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: '✓ Verified via Persona' };
      sourceLine = 'Persona completed the identity check. This step is auto-approved.';
    } else if (verifiedManually) {
      statusPill = { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: '✓ Verified manually by admin' };
      sourceLine = 'Admin manually approved identity documents. Persona was not used for this borrower.';
    } else if (idStatus === 'pending' || idStatus === 'in_progress') {
      statusPill = { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: '⏳ Persona in progress' };
      sourceLine = 'Borrower started Persona but has not completed verification yet.';
    } else if (idStatus === 'manual_review') {
      statusPill = { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: '⏳ Manual review pending' };
      sourceLine = 'Borrower submitted identity documents for manual admin review. Verify the documents to accept this step.';
    } else if (idStatus === 'declined' || idStatus === 'rejected') {
      statusPill = { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: '✗ Persona declined' };
      sourceLine = idVerification?.admin_rejection_notes || 'Persona did not approve. Review identity docs manually.';
    } else if ((idVerification && (idVerification.manual_front_url || idVerification.manual_back_url || idVerification.front_document_id))
        && !(idVerification && idVerification.manual_submitted_at)) {
      // 🔑 FROM THE IDENTITY RECORD (2026-10-22): photos uploaded at step 4 and never submitted are not an attempt.
      statusPill = { bg: '#fff7ed', border: '#fed7aa', color: '#7a5200', label: '📎 Photos uploaded, not submitted' };
      sourceLine = 'The borrower uploaded ID photos at step 4 but did not submit them. They complete nothing until submitted (or Persona verifies).';
    } else {
      statusPill = { bg: '#fff7ed', border: '#fed7aa', color: '#7a5200', label: '— Not started' };
      sourceLine = 'No Persona attempt on file. Borrower needs to complete Persona or admin needs to manually verify identity documents.';
    }
    return (
      <div style={{ flex: 1, minWidth: 260, padding: '14px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
          {`${person?.first_name || ''} ${person?.last_name || ''}`.trim() || person?.email || '—'}
        </div>
        <span style={{
          display: 'inline-block',
          fontSize: 11, fontWeight: 700,
          padding: '3px 10px', borderRadius: 9999,
          background: statusPill.bg,
          border: `1px solid ${statusPill.border}`,
          color: statusPill.color,
          marginBottom: 8,
        }}>
          {statusPill.label}
        </span>
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{sourceLine}</div>
        {methodIndicator && (
          <div style={{
            display: 'inline-block', marginTop: 8,
            fontSize: 11, fontWeight: 700,
            padding: '3px 10px', borderRadius: 9999,
            background: methodIndicator.bg,
            border: `1px solid ${methodIndicator.border}`,
            color: methodIndicator.color,
          }}>
            {methodIndicator.label}
          </div>
        )}
        {verifiedAt && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
            Updated {new Date(verifiedAt).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        🪪 Identity Verification
        {isPersonaApproved && sectionAccepted && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999,
            background: '#f0fde4', color: '#3d7a00', border: '1px solid #acf44e',
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            Auto-approved
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderRow('Primary', user)}
        {coBorrower && renderRow('Co-Borrower', coBorrower)}
      </div>
    </div>
  );
}

// Filter the merged aiExtractions array down to the extractions whose
// doc_type belongs to the active step's category. Reuses DOC_CATEGORIES
// so the AI panel and the SectionReviewPanel see the same scope of
// documents per step. Step 1 (Identity) hides this panel entirely (a
// dedicated IdentityVerificationPanel replaces it). When category is
// null/unknown, returns the full list so we don't accidentally hide
// extractions on a custom step.
function filterExtractionsForCategory(extractions, category) {
  if (!extractions?.length) return [];
  // No category → return empty rather than the whole list. A null/
  // undefined category means the caller has nothing meaningful to
  // scope by; the prior "return all" behavior caused step 6 to show
  // every extraction (including credit_report) whenever `step` was
  // undefined (e.g. activeStep advanced past activeSteps.length on
  // borrowers without a misc step), which read as "duplicate of step 5."
  if (!category) return [];
  const types = DOC_CATEGORIES[category] || [];
  if (!types.length) return [];
  const catOf = (t) => Object.keys(DOC_CATEGORIES).find(
    c => (DOC_CATEGORIES[c] || []).some(k => t.includes(k))
  ) || null;
  return extractions.filter(ex => {
    // Categorize by the document's CURRENT real type (document_doc_type, from
    // the GET join on documents.document_type) — authoritative — not the
    // extraction's stored doc_type, which can be stale if the doc was
    // re-tagged after extraction. Fall back to doc_type when the real type
    // isn't joined.
    const realType   = (ex.document_doc_type || ex.doc_type || '').toLowerCase();
    if (!types.some(k => realType.includes(k))) return false;
    // Stale-extraction guard: if the extraction was produced for a DIFFERENT
    // kind of document than the doc now is (its stored doc_type maps to a
    // different category), its fields + embedded preview are for the wrong
    // document — hide it everywhere. This is exactly the case where a vor_vom
    // extraction was left on a doc later re-tagged as a 1099: it leaked into
    // the Housing step as a "Verification of Rent" card previewing the 1099.
    const storedType = (ex.doc_type || '').toLowerCase();
    if (storedType && storedType !== realType) {
      const storedCat = catOf(storedType);
      if (storedCat && storedCat !== category) return false;
    }
    return true;
  });
}

// ExtractionCard — single AI extraction row with embedded PDF preview
// and a joint Approve button that approves BOTH the extraction AND the
// underlying document in one click (PATCH /ai-extractions/:id/approve
// AND PATCH /borrowers/:uid/documents/:docId/review). Pre-this commit
// the Approve only flipped the extraction's status; the document
// admin_status stayed 'unreviewed'. Now they move together.
//
// PDF preview: fetched as a Blob via axios (so the auth header travels
// with the request) and rendered through an Object URL in an iframe.
// Lazy: only fetches when the admin expands the preview, so we don't
// stream every PDF on every step load. Cleanup revokes the URL on
// unmount so we don't leak Blob handles.
function ExtractionCard({ ex, headers, API, id, onChanged }) {
  const [approving, setApproving] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const data = ex.extracted_data || {};
  const isPending = ex.status === 'pending_review';
  const isApproved = ex.status === 'approved';
  const isRejected = ex.status === 'rejected';
  const conf = data.confidence || ex.confidence || 'medium';

  // Approved cards collapse to a compact header so the pending ones
  // pop visually. Admin can still expand to inspect the data, PDF, and
  // audit line — just defaults to closed once the work is done.
  // Rejected stays collapsed by default too (same reasoning — terminal
  // state, no further action expected).
  const [collapsedSelf, setCollapsedSelf] = useState(isApproved || isRejected);
  // Re-collapse if the parent fetch flips this card to approved while
  // the panel is open (e.g. admin clicked Approve on this card and the
  // onChanged refetch brings back status='approved').
  useEffect(() => {
    if (isApproved || isRejected) setCollapsedSelf(true);
  }, [isApproved, isRejected]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const loadPdf = async () => {
    if (pdfUrl || pdfLoading || !ex.document_id) return;
    setPdfLoading(true);
    setPdfError('');
    try {
      const r = await axios.get(
        `${API}/api/admin/borrowers/${id}/documents/${ex.document_id}/view`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      setPdfUrl(url);
    } catch (e) {
      setPdfError(e.response?.data?.error || e.message || 'Could not load PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  const togglePdf = async () => {
    if (!pdfOpen && !pdfUrl) await loadPdf();
    setPdfOpen(o => !o);
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      // Approve extraction.
      await axios.patch(`${API}/api/admin/ai-extractions/${ex.id}/approve`, {}, { headers });
      // Approve the underlying document too. Fail-soft — if the doc-
      // review endpoint hiccups, the extraction approval still lands;
      // admin can re-flip the doc from the Documents tab if needed.
      if (ex.document_id) {
        try {
          await axios.patch(
            `${API}/api/admin/borrowers/${id}/documents/${ex.document_id}/review`,
            { action: 'accept' },
            { headers }
          );
        } catch (docErr) {
          console.warn('[Review] joint doc approve failed (non-fatal):', docErr.message);
        }
      }
      if (onChanged) onChanged();
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    const note = window.prompt('Reason (optional):');
    await axios.patch(`${API}/api/admin/ai-extractions/${ex.id}/reject`, { admin_notes: note }, { headers });
    if (onChanged) onChanged();
  };

  return (
    <div style={{ background: '#fff', border: `1px solid ${isPending ? '#fed7aa' : isApproved ? '#bbf7d0' : '#fecdd3'}`, borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setCollapsedSelf(c => !c)}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px',
          background: isPending ? '#fff7ed' : isApproved ? '#f0fdf4' : '#fff0f3',
          borderBottom: collapsedSelf ? 'none' : '1px solid #f3f4f6',
          border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', flex: 1 }}>{
          // vor_vom is the combined housing-verification type; the extractor
          // labels every one "Verification of Rent", which is wrong for
          // owner/mortgage borrowers. Show the accurate combined label.
          ((ex.document_doc_type || ex.doc_type || '').toLowerCase().includes('vor_vom'))
            ? 'Verification of Rent / Mortgage'
            : (ex.doc_label || ex.doc_type)
        }</span>
        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: conf === 'high' ? '#acf44e' : conf === 'medium' ? '#fff7ed' : '#fff0f3', color: conf === 'high' ? '#1a1a1a' : conf === 'medium' ? '#7a5200' : '#ff082f' }}>{conf}</span>
        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: isPending ? '#fff7ed' : isApproved ? '#f0fdf4' : '#fff0f3', color: isPending ? '#7a5200' : isApproved ? '#166534' : '#ff082f' }}>
          {isPending ? 'Pending' : isApproved ? 'Approved' : 'Rejected'}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280', minWidth: 12, textAlign: 'right' }}>{collapsedSelf ? '▼' : '▲'}</span>
      </button>
      {collapsedSelf ? null : (
      <div style={{ padding: '14px 16px' }}>
        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: isPending ? 12 : 0 }}>
          {Object.entries(data).filter(([k]) => !['confidence', 'notes'].includes(k)).map(([k, v]) => {
            if (v === null || v === undefined || v === '') return null;
            const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            // 🔑 Nested values readably (utils/extractionValue, 2026-10-26): key: value lines and lists, never "[object Object]".
            const val = isNested(v) ? null : scalarText(k, v);
            const hl = (ex.admin_fields || []).includes(k);
            return <div key={k} style={{ background: hl ? '#eff6ff' : '#f9fafb', border: hl ? '1px solid #bfdbfe' : '1px solid #f3f4f6', borderRadius: 6, padding: '6px 10px', gridColumn: isNested(v) ? '1 / -1' : undefined, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}{hl ? ' ⭐' : ''}</div>
              {val != null ? <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{val}</div> : (
                <div data-testid="uw-extraction-nested" style={{ fontSize: 12.5, color: '#111827', display: 'grid', gap: 2, marginTop: 2 }}>
                  {extractionLines(k, v).map((ln, i) => (
                    <div key={i} style={{ paddingLeft: ln.depth * 12, fontWeight: ln.group ? 700 : 500, overflowWrap: 'anywhere' }}>
                      {ln.bullet && !ln.group ? '• ' : ''}{ln.label ? `${ln.label}${ln.text ? ': ' : ''}` : ''}{ln.text}
                    </div>
                  ))}
                </div>
              )}
            </div>;
          })}
        </div>
        {data.notes && <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 10, fontSize: 12, color: '#7a5200' }}>💡 {data.notes}</div>}

        {/* PDF preview — collapsible, lazy-loaded. Sits inside the
            same card as the extracted data so the Approve action below
            visibly covers both. */}
        {ex.document_id && (
          <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={togglePdf}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: '#f9fafb',
                border: 'none', borderBottom: pdfOpen ? '1px solid #e5e7eb' : 'none',
                cursor: 'pointer', textAlign: 'left',
                fontSize: 12, fontWeight: 600, color: '#374151',
              }}
            >
              <span style={{ flex: 1 }}>Document preview</span>
              {pdfLoading && <span style={{ fontSize: 11, color: '#9ca3af' }}>Loading…</span>}
              <span style={{ fontSize: 11, color: '#6b7280' }}>{pdfOpen ? '▲' : '▼'}</span>
            </button>
            {pdfOpen && (
              <div style={{ background: '#fff' }}>
                {pdfError ? (
                  <div style={{ padding: 12, fontSize: 12, color: '#991b1b', background: '#fee2e2' }}>
                    ⚠ {pdfError}
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    title={`Document ${ex.document_id} preview`}
                    src={pdfUrl}
                    style={{ width: '100%', height: 520, border: 'none', display: 'block' }}
                  />
                ) : (
                  <div style={{ padding: 12, fontSize: 12, color: '#6b7280' }}>Loading PDF…</div>
                )}
              </div>
            )}
          </div>
        )}

        {isPending && (
          <div style={{ display: 'flex', gap: 8, paddingTop: 12, marginTop: 10, borderTop: '1px solid #f3f4f6' }}>
            <button onClick={handleApprove}
              disabled={approving}
              style={{ flex: 1, padding: 9, background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 8, cursor: approving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}>
              {approving ? 'Approving...' : '✓ Approve document + extraction'}
            </button>
            <button onClick={handleReject}
              style={{ padding: '9px 14px', background: '#fff0f3', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              ✗ Reject
            </button>
          </div>
        )}
        {ex.reviewed_by && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>{isApproved ? '✓' : '✗'} {ex.reviewed_by} · {new Date(ex.reviewed_at).toLocaleDateString()}{ex.admin_notes ? ` · ${ex.admin_notes}` : ''}</div>}
      </div>
      )}
    </div>
  );
}

function AIExtractionsPanel({ aiExtractions, API, headers, onChanged, category, id }) {
  // Scope to the current step. Without this filter, Step 1 (Identity)
  // showed AI extractions for every uploaded document — paystubs,
  // bank statements, tax returns — none of which is identity. Step
  // 2 (Income) showed bank statements alongside paystubs even though
  // assets belong to Step 3. Scoping each step to its own DOC_CATEGORIES
  // bucket means each panel only shows what the admin is reviewing
  // on that step.
  const scoped = useMemo(
    () => filterExtractionsForCategory(aiExtractions, category),
    [aiExtractions, category]
  );
  const pendingCount = scoped.filter(e => e.status === 'pending_review').length;
  const [collapsed, setCollapsed] = useState(pendingCount === 0);
  if (!scoped.length) return null;
  return (
    <div style={{ marginBottom: 18, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: '#f9fafb',
          border: 'none', borderBottom: collapsed ? 'none' : '1px solid #e5e7eb',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', flex: 1 }}>
          🤖 AI Extracted Data
        </span>
        {pendingCount > 0 && (
          <span style={{ background: '#fff7ed', color: '#7a5200', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            {pendingCount} pending
          </span>
        )}
        <span style={{ fontSize: 12, color: '#6b7280' }}>{collapsed ? '▼' : '▲'}</span>
      </button>
      {!collapsed && (
        <div style={{ padding: 14 }}>
          {scoped.map(ex => (
            <ExtractionCard
              key={ex.id}
              ex={ex}
              headers={headers}
              API={API}
              id={id}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// DeclarationsReviewCard — Miscellaneous step. Surfaces every 1003
// declaration the borrower (and co-borrower) answered "Yes" to, with
// the reported timing bucket and any required Letter of Explanation +
// its completion status, so the admin reviews them before approving
// the Miscellaneous section. Read-only summary — the Approve / Flag
// action lives on the SectionAIReviewCard that follows it.
// ───────────────────────────────────────────────────────────────
const DECL_REVIEW_LABELS = {
  bankruptcy:         'Declared bankruptcy',
  foreclosure:        'Property foreclosed or deed-in-lieu',
  behind_on_mortgage: 'Behind on a mortgage / in default on a home loan',
  federal_debt:       'Delinquent on federal debt',
  lawsuit_current:    'Currently a party to a lawsuit',
  occupy_property:    'Property will not be the primary residence',
  borrowed_down:      'Borrowing money for the down payment',
  cosigner:           'Co-signer on another loan',
  alimony:            'Obligated to pay alimony / child support',
  undisclosed_debts:  'Has undisclosed debts',
};
const BANKRUPTCY_TIMING_LABELS = {
  less_than_1: 'Less than 1 year ago',
  '1_to_3':    '1–3 years ago',
  more_than_3: 'More than 3 years ago',
};
const FORECLOSURE_TIMING_LABELS = {
  less_than_2: 'Less than 2 years ago',
  '2_to_4':    '2–4 years ago',
  more_than_4: 'More than 4 years ago',
};
// Declaration key → the CANONICAL category of its letter (backend constants/loxCategories; the staff payload's
// canonical_category), and the form_data flag that records whether a LOX is required.
// 🔑 LOX Level 2 (push C item 6 — the category drift): these were 'bankruptcy_lox' / 'foreclosure_lox' /
// 'mortgage_default_lox', spellings no generator writes (utils/creditConditions files a bankruptcy or foreclosure
// letter under 'extenuating' and a mortgage-late letter under 'mortgage_lates'), so the declaration never found its
// letter and always read "required, not on file".
const DECL_LOX = {
  bankruptcy:         { category: 'extenuating',    flag: 'requires_bankruptcy_lox' },
  foreclosure:        { category: 'extenuating',    flag: 'requires_foreclosure_lox' },
  behind_on_mortgage: { category: 'mortgage_lates', flag: 'requires_mortgage_default_lox' },
};

function declarationYesList(fd) {
  const decl = (fd && fd.declarations) || {};
  return Object.keys(DECL_REVIEW_LABELS)
    .filter(k => decl[k] === true)
    .map(k => {
      let timing = null;
      if (k === 'bankruptcy')  timing = BANKRUPTCY_TIMING_LABELS[fd?.bankruptcy_discharge_timing] || null;
      if (k === 'foreclosure') timing = FORECLOSURE_TIMING_LABELS[fd?.foreclosure_timing] || null;
      const loxMeta = DECL_LOX[k] || null;
      return {
        key: k,
        label: DECL_REVIEW_LABELS[k],
        timing,
        loxCategory: loxMeta?.category || null,
        loxRequired: loxMeta ? !!fd?.[loxMeta.flag] : false,
      };
    });
}

function DeclarationColumn({ title, accent, items, loxConditions, loxAvailable }) {
  return (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No “Yes” declaration responses.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it) => {
            const lox = loxAvailable && it.loxCategory
              ? (loxConditions || []).find(c => (c.canonical_category || c.category || '').toLowerCase() === it.loxCategory)
              : null;
            return (
              <div key={it.key} style={{ border: '1px solid #fecaca', background: '#fff7f7', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', borderRadius: 9999, padding: '1px 8px' }}>YES</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{it.label}</span>
                </div>
                {it.timing && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Reported timing: <strong>{it.timing}</strong></div>
                )}
                {(it.loxCategory && (lox || it.loxRequired)) && (
                  <div style={{ marginTop: 6 }}>
                    {lox ? (
                      <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 9999, padding: '2px 10px',
                        background: lox.lox_completed ? '#f0fdf4' : '#fffbeb',
                        color: lox.lox_completed ? '#166534' : '#92400e',
                        border: `1px solid ${lox.lox_completed ? '#bbf7d0' : '#fde68a'}` }}>
                        {lox.lox_completed ? '✓ LOX completed' : '○ LOX pending borrower'}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 9999, padding: '2px 10px', background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
                        LOX required
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DeclarationsReviewCard({ formData, coBorrower, loxConditions, primaryName }) {
  const primaryItems = declarationYesList(formData);
  const coItems = coBorrower ? declarationYesList(coBorrower) : [];
  // No "Yes" answers on either side → nothing to surface.
  if (primaryItems.length === 0 && coItems.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #fecdd3', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#991b1b' }}>Declaration “Yes” Responses</span>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
        Answered “Yes” to the 1003 declarations below. Review each against the credit pull and the corresponding Letter of Explanation, then approve the Miscellaneous section.
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <DeclarationColumn title={primaryName || 'Borrower'} accent="#991b1b" items={primaryItems} loxConditions={loxConditions} loxAvailable={true} />
        {coBorrower && (
          <DeclarationColumn title={coBorrower.first_name || 'Co-borrower'} accent="#3a6ad9" items={coItems} loxConditions={[]} loxAvailable={false} />
        )}
      </div>
    </div>
  );
}

// ReviewModule (default export)
// ───────────────────────────────────────────────────────────────


/**
 * 🔑 ONE WIZARD STEP'S PANELS, AS A COMPONENT (2026-10-24). The wizard below renders it for its active step; the guided
 * Review tab (components/admin/guidedReview) renders it inside its step sections — the same panels, the same props, the
 * same actions (Jesse: the wizard's steps "become the tab's own sections; no overlay").
 * stepId: identity | income | assets | housing | credit | misc.
 */
export function ReviewStepBody({ stepId, user, coBorrower, formData, reviewSections, documents, additionalNeeds, loxConditions,
  headers, API, id, onSectionUpdated, aiExtractions, onExtractionChanged, idVerification }) {
  const step = stepId ? { id: stepId } : null;
  const parsedAnalysis = useMemo(() => {
    const rc = user?.readycheck;
    if (!rc) return null;
    const raw = rc.latestAnalysis || rc.phase1?.latestAnalysis?.analysis_json || rc.phase1?.latestAnalysis?.analysis || rc.phase1?.latestAnalysis || null;
    if (!raw) return null;
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (_) { return null; }
  }, [user]);
  return (
    <>
        {/* AI Extracted Data — scoped to the active step's category so
            each step only renders the extractions admin is reviewing
            on that step. Now shown on Step 1 (Identity) too, so the
            underwriter sees the ID extractions/findings alongside the
            IdentityVerificationPanel below. AIExtractionsPanel self-hides
            when there are no extractions for the step's category. */}
        {step?.id && (
          <AIExtractionsPanel
            key={`extr-${step.id}`}
            aiExtractions={aiExtractions}
            API={API}
            headers={headers}
            onChanged={onExtractionChanged}
            category={step.id}
            id={id}
          />
        )}

        {step?.id === 'identity' && (
          <IdentityVerificationPanel
            key="id-panel"
            user={user}
            coBorrower={coBorrower}
            idVerification={idVerification}
            reviewSections={reviewSections}
            headers={headers}
            API={API}
            id={id}
            onSectionUpdated={onSectionUpdated}
          />
        )}

        {/* Identity now mirrors the other steps — the SAME generic
            SectionAIReviewCard that Housing uses. It surfaces the ReadyCheck
            AI identity finding (the identity sections[] entry: status +
            narrative + per-doc items), an embedded preview of the uploaded ID
            (via SECTION_PREVIEW_DOC_KEYWORDS.identity), and Approve / Flag
            actions. Accepting here is what clears the AI-verified path (files
            ReadyCheck-AI approved are intentionally left review_sections.
            identity='pending' for the underwriter to approve in-module). The
            Persona badge + verification-method indicator live in the panel
            above; the AIExtractionsPanel above self-hides when identity has no
            document extractions. */}
        {step?.id === 'identity' && (
          <SectionAIReviewCard
            key="section-identity"
            sectionKey="identity"
            sectionLabel="Identity Review"
            approveLabel="Approve identity"
            flagPrompt="What's the concern with the ID? (e.g. name mismatch, expired, blurry / illegible, not a government photo ID)"
            missingCopy={{
              title: 'No identity analysis on file yet',
              body: "The document check hasn't produced an identity review. Once the borrower uploads a government photo ID and the AI run completes — or they verify via Persona — the AI's verdict and per-doc notes will populate here.",
            }}
            user={user}
            // 🔑 Step-4 ID photos never submitted are not offered for approval (identity from the identity record).
            documents={idVerification && !idVerification.manual_submitted_at
              ? (documents || []).filter((d) => String(d.source || '') !== 'manual_id') : documents}
            fullAnalysis={parsedAnalysis}
            reviewSections={reviewSections}
            headers={headers}
            API={API}
            id={id}
            onSectionUpdated={onSectionUpdated}
          />
        )}

        {/* Housing / Credit / Misc — generic SectionAIReviewCard. AI
            section notes (status + narrative + items) + embedded
            preview of the primary doc + Approve / Flag actions. Each
            step instance passes its own copy. */}
        {step?.id === 'housing' && (
          <PrivateMortgageVomCard
            additionalNeeds={additionalNeeds}
            id={id}
            headers={headers}
            API={API}
            onSectionUpdated={onSectionUpdated}
          />
        )}
        {step?.id === 'housing' && (
          <SectionAIReviewCard
            key="section-housing"
            sectionKey="housing"
            sectionLabel="Housing Review"
            approveLabel="Approve housing"
            flagPrompt="What's the concern with housing? (e.g. missing months on the VOR, late-pay history, address mismatch)"
            missingCopy={{
              title: 'No housing analysis on file yet',
              body: "The document check hasn't produced a housing section. Once the AI run completes, the AI's notes and per-doc verdicts will populate here.",
            }}
            user={user}
            documents={documents}
            fullAnalysis={parsedAnalysis}
            reviewSections={reviewSections}
            headers={headers}
            API={API}
            id={id}
            onSectionUpdated={onSectionUpdated}
          />
        )}
        {step?.id === 'credit' && (
          <CreditReviewCard
            key="credit-review"
            user={user}
            reviewSections={reviewSections}
            headers={headers}
            API={API}
            id={id}
            onSectionUpdated={onSectionUpdated}
            loxConditions={loxConditions}
          />
        )}
        {step?.id === 'misc' && (
          <DeclarationsReviewCard
            key="decl-review"
            formData={formData}
            coBorrower={coBorrower}
            loxConditions={loxConditions}
            primaryName={user?.first_name}
          />
        )}
        {step?.id === 'misc' && (
          <SectionAIReviewCard
            key="section-misc"
            sectionKey="misc"
            sectionLabel="Miscellaneous Review"
            approveLabel="Approve miscellaneous"
            flagPrompt="What's the concern? (e.g. declaration LOX answer doesn't match the credit pull, missing LO-requested item)"
            missingCopy={{
              title: 'No miscellaneous items on file',
              body: 'The borrower has no declaration LOXes or LO-requested misc items that require review.',
            }}
            user={user}
            documents={documents}
            fullAnalysis={parsedAnalysis}
            reviewSections={reviewSections}
            headers={headers}
            API={API}
            id={id}
            onSectionUpdated={onSectionUpdated}
          />
        )}

    </>
  );
}

export default function ReviewModule({
  user,
  coBorrower,
  formData,          // parsed borrower form_data — declarations + LOX timing for the Misc step card
  reviewSections,
  documents,
  additionalNeeds,   // eslint-disable-line no-unused-vars
  conditions,        // eslint-disable-line no-unused-vars
  loxConditions,     // credit_conditions where condition_type='lox', loaded by admin.js
  hasMisc,           // borrower has Misc LOXes / docs (drives whether Step 6 renders)
  loanConnection,    // eslint-disable-line no-unused-vars
  headers,
  API,
  id,
  onSectionUpdated,
  onClose,
  aiExtractions,     // AI-parsed document data; rendered as a collapsible panel above the step content
  onExtractionChanged, // callback the parent uses to refetch extractions + form data after approve/reject
  idVerification,    // identity_verifications row — drives the Step 1 Persona vs manual panel + auto-approve
  // 🔑 GUIDED FILES (Jesse, 2026-10-24 strip-back — the Review tab's actions live here now):
  //   guided.stepTop   { [stepId]: node } shown above a step's own panels (fraud on Identity, steps 5–6 on Income, …)
  //   guided.extraSteps [{ id, label, shortLabel, node }] after the section steps (Documents, Decision, Consents).
  //   Extra steps have no review section: always reachable, never gate Next, never count towards "complete".
  guided = null,
  initialStepId = null, // open on this step (the tab's "Open in Underwriting Module" passes the guided step's)
}) {
  // activeSteps = STEPS minus the Misc step on borrowers without
  // any Misc items. Wizard collapses to 5 steps in that case so
  // admin doesn't click through an empty section review.
  const baseSteps = useMemo(() => getActiveSteps(!!hasMisc), [hasMisc]);
  const activeSteps = useMemo(() => (guided && guided.extraSteps ? [...baseSteps, ...guided.extraSteps] : baseSteps), [baseSteps, guided]);
  const derived = useMemo(
    () => deriveReviewStep(reviewSections, baseSteps),
    [reviewSections, baseSteps]
  );
  const askedIdx = initialStepId ? activeSteps.findIndex((x) => x.id === initialStepId) : -1;
  const initialStep = askedIdx >= 0 ? askedIdx + 1 : derived === 'complete' ? baseSteps.length : derived;
  const [activeStep, setActiveStep] = useState(initialStep);
  // 🔑 DOCS IS NOT A STEP (Jesse, 2026-10-26): "All documents" on every step opens the read-only list (guided.allDocuments),
  // with "Re-run the step-5 check" beside it (guided.recheck). An old link to the Docs step opens the list.
  const [allDocsOpen, setAllDocsOpen] = useState(initialStepId === 'documents');
  // 🔑 The phone step list (below 640px) — closed by default, closes when a step is tapped.
  const [stepListOpen, setStepListOpen] = useState(false);
  // 🔑 A GUIDED FILE'S OPEN FLAGS, ON THE STEP THAT OWNS EACH DOCUMENT (backend utils/fraudFlagPlacement, Jesse 2026-10-26):
  // the file-wide banner, the step strip (a ✓ only when the step is accepted AND none of its flags is open) and each
  // step's inline list read the one set.
  const [flagRefresh, setFlagRefresh] = useState(0);
  const placed = usePlacedFlags(API, guided ? id : null, headers, flagRefresh);
  // …and each step's own open requests, explanations and letters (backend utils/moduleItems).
  const stepItems = useModuleItems(API, guided ? id : null, headers, flagRefresh);
  const reloadGuided = async () => { setFlagRefresh((n) => n + 1); if (onSectionUpdated) await onSectionUpdated(); };
  // 🔑 AN EXTRA STEP'S OWN STATUS (2026-10-27): a guided extra step with a statusUrl (the "1003 and summary" step —
  // routes/urla1003) reports { open, accepted } for the strip and the phone list; read without side effects.
  const statusUrls = guided && guided.extraSteps ? guided.extraSteps.filter((x) => x.statusUrl).map((x) => `${x.id}|${x.statusUrl}`).join(',') : '';
  const [extraStatus, setExtraStatus] = useState({});
  useEffect(() => {
    if (!statusUrls) return undefined;
    let live = true;
    (async () => {
      const out = {};
      for (const pair of statusUrls.split(',')) {
        const [sid, url] = [pair.slice(0, pair.indexOf('|')), pair.slice(pair.indexOf('|') + 1)];
        try { const r = await axios.get(url, { headers }); out[sid] = { open: Number(r.data && r.data.open) || 0, done: !!(r.data && r.data.accepted) }; } catch (_) { /* unread: no status */ }
      }
      if (live) setExtraStatus(out);
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUrls, flagRefresh]);
  const goToModule = (moduleId) => { const i = activeSteps.findIndex((x) => x.id === moduleId); if (i >= 0) setActiveStep(i + 1); };

  // Auto-advance on data shift removed. Previously this hook bumped
  // activeStep up whenever the derived step advanced (e.g. admin
  // clicks Accept on Step 1 → review_sections updates → derived
  // becomes 2 → activeStep jumps to 2). Admins flagged that as
  // jarring — they want the affordance of clicking Accept to leave
  // them on the same step so they can review what they just did
  // or use the Flag path if they change their mind. Smart-resume on
  // initial mount (lowest incomplete step) still works via
  // initialStep above; advancement post-mount is admin-driven via
  // Next / tile-click only.

  // Clamp activeStep to the valid range. activeSteps can shrink (e.g.
  // a borrower's misc-step trigger resolves to false on a later
  // refresh), and if activeStep stays at the now-out-of-range index
  // step would be undefined → AIExtractionsPanel fell through to
  // showing every extraction. Snap forward via useEffect so the
  // index sticks rather than re-clamping on every render.
  const safeStep = Math.min(Math.max(1, activeStep), Math.max(1, activeSteps.length));
  useEffect(() => {
    if (activeStep !== safeStep) setActiveStep(safeStep);
  }, [activeStep, safeStep]);
  const step = activeSteps[safeStep - 1];

  // Parsed analysis_json from the borrower's latest ReadyCheck run.
  // Powers the Income Calculation card on Step 2 (qualifyingMonthly +
  // breakdown).
  //
  // Two shapes get passed to this surface depending on which endpoint
  // populated user.readycheck:
  //   1. admin.js readycheckSummary (the one we receive here, per the
  //      AdminBorrowerProfile fetch) — FLAT shape, rc.latestAnalysis
  //      is the parsed JSONB directly at top level.
  //   2. readycheck/status — NESTED shape, rc.phase1.latestAnalysis
  //      is a doc with an analysis_json field.
  // Try the flat shape first since that's what actually arrives in
  // this component; fall back to the nested shape for forward-compat
  // with any future caller that hands the readycheck/status response
  // through.
  // (The analysis is parsed inside ReviewStepBody, which renders this step's panels.)

  // 🔑 A GUIDED FILE: THE APPROVED REDESIGN (Jesse, 2026-10-26 — the approved Underwriting Module redesign mockups). A white
  // top bar, the step strip (a ✓ only when the step's section is accepted and none of its flags is open; otherwise the
  // open-flag count in red), the file-wide fraud banner on every step, and each step's guided body. Steps move freely:
  // staff review runs in parallel with the borrower's steps 6–8 and must be complete before an offer (utils/fileReview).
  if (guided) {
    const accepted = (sid) => { const st = activeSteps.find((x) => x.id === sid); const row = st && st.primary ? reviewSections?.find((r) => r.section === st.primary) : null;
      return !!row && ['accepted', 'approved'].includes(row.status); };
    const top = step && guided.stepTop ? guided.stepTop[step.id] : null;
    const topNode = typeof top === 'function' ? top({ flags: placed.flags, items: stepItems, reload: reloadGuided, goTo: goToModule }) : top;
    return (
      <div data-testid="uw-module-guided" style={{ width: '100%', fontFamily: GT.font, color: GT.ink, background: GT.ground, borderRadius: 16, overflow: 'hidden' }}>
        {/* 🔑 PHONE (Jesse, 2026-10-27 — the approved mockups): below 640px the wrapped chip rows become one control,
            "<n> of <total> · <step> · <k> open ▾", opening the full step list with each step's status (done / open count /
            not started); tapping a step goes there and closes the list. 640px and up keeps the tabs. */}
        <style>{'.uwm-stepctl{display:none}@media (max-width:639px){.uwm-tabs{display:none!important}.uwm-stepctl{display:grid}.uwm-top{padding:16px 20px!important}.uwm-body{padding:18px 20px!important}}'}</style>
        <div className="uwm-top" style={{ background: GT.white, borderBottom: `1px solid ${GT.line}`, padding: '16px 28px', display: 'grid', gap: 2 }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: GT.navy }}>Underwriting Module</div>
          {/* 🔑 The close (✕) on the same line as the name(s); the loan number on its own line, never wrapped; no
              "guided" in staff screens — guided is the only flow. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div data-testid="uw-module-names" style={{ fontSize: 22, fontWeight: 800, minWidth: 0 }}>
              {user?.first_name} {user?.last_name}{coBorrower ? ` + ${coBorrower.first_name} ${coBorrower.last_name}` : ''}
            </div>
            {onClose && (
              <button type="button" data-testid="uw-module-close" aria-label="Close the Module" onClick={onClose}
                style={{ flex: 'none', minHeight: 44, minWidth: 44, borderRadius: 10, border: `1px solid ${GT.line}`, background: GT.white, color: GT.ink, font: `600 18px ${GT.font}`, cursor: 'pointer' }}>✕</button>
            )}
          </div>
          {user?.loan_number && <div data-testid="uw-module-loan-number" style={{ fontSize: 15, fontWeight: 500, whiteSpace: 'nowrap' }}>{user.loan_number}</div>}
        </div>
        {(() => {
          const statusOf = (st) => {
            const sx = extraStatus[st.id] || {};
            const n = (placed.counts[st.id] || 0) + (stepItems.counts[st.id] || 0) + (Number(st.open) || 0) + (Number(sx.open) || 0);
            const done = (st.primary ? accepted(st.id) : (st.done === true || sx.done === true)) && n === 0;
            const row = st.primary ? reviewSections?.find((r) => r.section === st.primary) : null;
            const started = done || n > 0 || (row && row.status && !['pending', 'locked'].includes(row.status)) || st.started === true;
            return { n, done, word: done ? 'Done' : n > 0 ? `${n} open` : started ? 'In review' : 'Not started' };
          };
          const cur = activeSteps[safeStep - 1];
          const cs = cur ? statusOf(cur) : { n: 0 };
          return (<>
            <div className="uwm-stepctl" style={{ padding: '12px 20px', background: GT.white, borderBottom: `1px solid ${GT.line}`, gap: 8 }}>
              <button type="button" data-testid="uw-module-step-control" aria-expanded={stepListOpen} onClick={() => setStepListOpen((v) => !v)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', minHeight: 48, padding: '0 14px', border: `1px solid ${GT.line}`, borderRadius: 10, background: GT.white, color: GT.ink, font: `700 14px ${GT.font}`, textAlign: 'left', cursor: 'pointer' }}>
                <span>{safeStep} of {activeSteps.length} · {cur ? cur.label : ''}{cs.n > 0 ? ` · ${cs.n} open` : ''}</span>
                <span aria-hidden="true">{stepListOpen ? '▴' : '▾'}</span>
              </button>
              {stepListOpen && (
                <div data-testid="uw-module-step-list" role="listbox" style={{ border: `1px solid ${GT.line}`, borderRadius: 10, background: GT.white, overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,.12)' }}>
                  {activeSteps.map((st, i) => {
                    const x = statusOf(st);
                    const on = i + 1 === safeStep;
                    return (
                      <button key={st.id} type="button" role="option" aria-selected={on} data-testid={`uw-module-step-option-${st.id}`} data-status={x.word}
                        onClick={() => { setActiveStep(i + 1); setAllDocsOpen(false); setStepListOpen(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '0 14px', border: 'none', borderBottom: i < activeSteps.length - 1 ? `1px solid ${GT.line}` : 'none', background: on ? GT.ground : GT.white, color: GT.ink, font: `${on ? 800 : 600} 14px ${GT.font}`, textAlign: 'left', cursor: 'pointer' }}>
                        <span aria-hidden="true" style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                          background: x.n > 0 ? GT.red : x.done ? GT.green : GT.white, color: x.n > 0 ? GT.white : GT.ink, border: x.n > 0 || x.done ? 'none' : `1px solid ${GT.ink}` }}>{x.n > 0 ? x.n : x.done ? '✓' : ''}</span>
                        {i + 1} · {st.label}
                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700 }}>{x.word}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="uwm-tabs" role="tablist" style={{ background: GT.white, borderBottom: `1px solid ${GT.line}`, padding: '0 28px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {activeSteps.map((st, i) => {
                // the strip icon: its open flags AND its open requests, explanations and letters
                const { n, done } = statusOf(st);
                const active = i + 1 === activeStep;
                return (
                  <button key={st.id} type="button" role="tab" aria-selected={active} data-testid={`uw-module-tile-${st.id}`} data-active={active ? 'true' : 'false'}
                    data-done={done ? 'true' : 'false'} data-flags={n} onClick={() => { setActiveStep(i + 1); setAllDocsOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 14px', background: 'none', border: 'none',
                      borderBottom: `3px solid ${active ? GT.blue : 'transparent'}`, color: GT.ink, font: `${active ? 800 : 600} 14px ${GT.font}`, cursor: 'pointer' }}>
                    <span aria-hidden="true" style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                      background: n > 0 ? GT.red : done ? GT.green : GT.line, color: n > 0 ? GT.white : GT.ink }}>{n > 0 ? n : done ? '✓' : '○'}</span>
                    {st.shortLabel && st.shortLabel.length > 3 ? st.shortLabel : st.label}
                  </button>
                );
              })}
            </div>
          </>);
        })()}
        <div className="uwm-body" style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {(guided.allDocuments || guided.recheck) && (
            <div data-testid="uw-module-docs-bar" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              {guided.allDocuments && (
                <button type="button" data-testid="uw-all-documents-link" aria-haspopup="dialog" aria-expanded={allDocsOpen} onClick={() => setAllDocsOpen(true)}
                  style={{ minHeight: 44, padding: '0 16px', borderRadius: 10, border: `1px solid ${GT.ink}`, background: GT.white, color: GT.ink, font: `700 14px ${GT.font}`, cursor: 'pointer' }}>
                  {`All documents${Number.isFinite(guided.documentCount) ? ` · ${guided.documentCount}` : ''}`}
                </button>
              )}
              {guided.recheck}
            </div>
          )}
          {/* 🔑 "ALL DOCUMENTS" IS ITS OWN OVERLAY (LO plan v7 item 9, Jesse 2026-10-28): titled "All documents", with Close,
              over the step it was opened from — the step stays where it is underneath, so Close returns to it. No
              "Re-run the step-5 check" in it (that stays on the steps). */}
          {allDocsOpen && guided.allDocuments && (
            <div data-testid="uw-all-documents-overlay" role="dialog" aria-modal="true" aria-labelledby="uw-all-documents-title"
              style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ width: '100%', maxWidth: 1060, maxHeight: '90vh', overflowY: 'auto', borderRadius: 14, background: GT.white, color: GT.ink, fontFamily: GT.font, boxShadow: '0 25px 60px rgba(0,0,0,0.35)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '16px 22px', borderBottom: `1px solid ${GT.line}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div id="uw-all-documents-title" style={{ fontSize: 20, fontWeight: 800 }}>All documents</div>
                    <div data-testid="uw-all-documents-sub" style={{ fontSize: 14, marginTop: 2 }}>
                      {[`${user?.first_name || ''} ${user?.last_name || ''}`.trim(), user?.loan_number,
                        Number.isFinite(guided.documentCount) ? `${guided.documentCount} document${guided.documentCount === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button type="button" data-testid="uw-all-documents-close" onClick={() => setAllDocsOpen(false)}
                    style={{ flex: 'none', minHeight: 44, padding: '0 16px', borderRadius: 10, border: `1px solid ${GT.ink}`, background: GT.white, color: GT.ink, font: `700 14px ${GT.font}`, cursor: 'pointer' }}>Close</button>
                </div>
                <div data-testid="uw-all-documents" style={{ display: 'grid', gap: 14, padding: '18px 22px 22px' }}>{guided.allDocuments}</div>
              </div>
            </div>
          )}
          {(<>
          <FraudBanner flags={placed.flags} onGoTo={(m, fid) => { goToModule(m); setTimeout(() => { const el = document.getElementById(`uw-flag-${fid}`); if (el) el.scrollIntoView({ block: 'start' }); }, 50); }} />
          {topNode ? <div data-testid={`uw-module-guided-${step.id}`} style={{ display: 'grid', gap: 20 }}>{topNode}</div> : null}
          {step && !step.primary ? (step.node ? (
            <div data-testid={`uw-module-step-${step.id}`} style={{ display: 'grid', gap: 14, background: GT.white, borderRadius: 12, padding: 20 }}>{step.node}</div>
          ) : null) : step && step.id !== 'income' && step.id !== 'assets' ? (
            <div style={{ background: GT.white, border: `1px solid ${GT.line}`, borderRadius: 12, padding: 20 }}>
              <ReviewStepBody stepId={step?.id} user={user} coBorrower={coBorrower} formData={formData} reviewSections={reviewSections}
                documents={documents} additionalNeeds={additionalNeeds} loxConditions={loxConditions} headers={headers} API={API} id={id}
                onSectionUpdated={reloadGuided} aiExtractions={aiExtractions} onExtractionChanged={onExtractionChanged} idVerification={idVerification} />
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button type="button" onClick={() => setActiveStep((x) => Math.max(1, x - 1))} disabled={activeStep <= 1}
              style={{ minHeight: 44, padding: '0 16px', borderRadius: 10, border: `1px solid ${GT.ink}`, background: GT.white, color: GT.ink, font: `700 14px ${GT.font}`, cursor: activeStep <= 1 ? 'not-allowed' : 'pointer', opacity: activeStep <= 1 ? 0.5 : 1 }}>← Back</button>
            {activeStep < activeSteps.length && (
              <button type="button" onClick={() => setActiveStep((x) => x + 1)}
                style={{ minHeight: 44, padding: '0 16px', borderRadius: 10, border: 'none', background: GT.blue, color: GT.white, font: `700 14px ${GT.font}`, cursor: 'pointer' }}>Next: {activeSteps[activeStep]?.label} →</button>
            )}
          </div>
          </>)}
        </div>
      </div>
    );
  }

  // The classic wizard (no `guided` prop) is gone (cleanup Phase 2): the Review tab always passes `guided`.
  return null;
}

// ── 🔑 COMPENSATING FACTORS ON A CREDIT CONDITION (2026-10-20) ─────────────────────────────────────────────────
// Shown only on a condition whose source_flag.compFactorsRequired > 0 (multiple 1x30 mortgage lates → 2; collections /
// charge-offs within 12 months → 1). Staff pick from afmMatrix.COMPENSATING_FACTORS (served by the API) and say why it
// applies; the server refuses Accept until enough are recorded.
// 🔑 COMPENSATING FACTORS, ONE PANEL (AFM v2): the factors the FILE'S OWN FIGURES meet, read-only with the figure and its
// source (backend utils/compensatingFactors fileFactors — Hannah's review 2026-11-02), then what underwriting has recorded.
// Recording one stays underwriting's (AFM v2 §3: "underwriting records them from this list") — behind "Add factor".
export function CompFactorPanel({ condition, borrowerId, headers, api, onChange }) {
  const sf = (() => { const v = condition && condition.source_flag; if (typeof v === 'string') { try { return JSON.parse(v); } catch (_) { return null; } } return v; })();
  const required = Number(sf && sf.compFactorsRequired) || 0;
  const [data, setData] = useState(null);
  const [factor, setFactor] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const base = `${api}/api/admin/borrowers/${borrowerId}/credit-conditions/${condition && condition.id}/comp-factors`;
  const load = () => axios.get(base, { headers }).then((r) => setData(r.data)).catch(() => setError('Could not load the compensating factors.'));
  useEffect(() => { if (required && borrowerId && condition && condition.id) load(); }, [required, borrowerId, condition && condition.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!required) return null;
  const recorded = (data && data.recorded) || [];
  const detected = (data && data.detected) || [];
  const thresholds = Object.fromEntries(((data && data.thresholds) || []).map((t) => [t.label, t.threshold]));
  const options = ((data && data.options) || []).filter((o) => !recorded.some((r) => r.factor === o));
  const record = async () => {
    setBusy(true); setError(null);
    try { await axios.post(base, { factor, note }, { headers }); setFactor(''); setNote(''); setAdding(false); await load(); onChange && onChange(); }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || 'Could not record it.'); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true); setError(null);
    try { await axios.delete(`${base}/${id}`, { headers }); await load(); onChange && onChange(); }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || 'Could not remove it.'); }
    finally { setBusy(false); }
  };
  const done = recorded.length >= required;
  const field = { minHeight: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${GT.ink}`, font: `500 14px ${GT.font}`, color: GT.ink, width: '100%', minWidth: 0, boxSizing: 'border-box' };
  return (
    <div data-testid={`comp-factors-${condition.id}`} style={{ display: 'grid', gap: 6, padding: '12px 14px', background: GT.ground, borderRadius: 8, fontSize: 13, color: GT.ink, fontFamily: GT.font }}>
      <div style={{ fontWeight: 700 }}>
        Compensating factors: {recorded.length} of {required} recorded{done ? '' : ' — required before Accept'}
      </div>
      {detected.length ? (
        <div data-testid={`comp-factors-file-${condition.id}`} style={{ display: 'grid', gap: 2 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: GT.navy }}>From the file</div>
          {detected.map((d) => <div key={d.key} data-testid="comp-factor-file-line">{d.line}</div>)}
        </div>
      ) : null}
      {recorded.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <span style={{ fontWeight: 600 }} title={thresholds[r.factor] || ''}>✓ {r.factor}</span>
          <span>— {r.note}</span>
          <button type="button" onClick={() => remove(r.id)} disabled={busy} style={{ marginLeft: 'auto', font: `600 12px ${GT.font}`, background: 'none', border: 'none', color: GT.ink, textDecoration: 'underline', cursor: 'pointer' }}>Remove</button>
        </div>
      ))}
      {!done && !adding ? (
        <button type="button" data-testid={`comp-factor-add-${condition.id}`} onClick={() => setAdding(true)}
          style={{ justifySelf: 'start', padding: 0, background: 'none', border: 'none', color: GT.blue, textDecoration: 'underline', font: `600 13px ${GT.font}`, cursor: 'pointer' }}>
          Add factor
        </button>
      ) : null}
      {!done && adding && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
          <select aria-label="Compensating factor" value={factor} onChange={(e) => setFactor(e.target.value)} style={field}>
            <option value="">Pick a compensating factor…</option>
            {options.map((o) => <option key={o} value={o}>{thresholds[o] ? `${o} — ${thresholds[o]}` : o}</option>)}
          </select>
          {factor && thresholds[factor] && (
            <div data-testid="comp-factor-threshold">Threshold: {thresholds[factor]}</div>
          )}
          <textarea aria-label="Why it applies" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why it applies to this file (e.g. 14 months of reserves after closing)" style={{ ...field, minHeight: 64, padding: '8px 10px' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setAdding(false)} disabled={busy} style={GBTN()}>Cancel</button>
            <button type="button" onClick={record} disabled={busy || !factor || note.trim().length < 10} style={GBTN('primary', busy || !factor || note.trim().length < 10)}>
              {busy ? 'Saving…' : 'Record factor'}
            </button>
          </div>
        </div>
      )}
      {error && <div role="alert" style={{ fontWeight: 700 }}>{error}</div>}
    </div>
  );
}
